// ── Industry Closings ──────────────────────────────────────────────────────
//
// Who is lending in this market, to which sectors, at what size, and how that
// has moved. Sourced from the ABF Journal deal chart — published closings, not
// our own portfolio.
//
// EVERYTHING IS BY QUARTER.
//
//   The rows carried over from the old database have no closing date: the
//   quarter was encoded in the DATE column as a January day, which the
//   migration unpacked into closingyear/closingquarter. Rows loaded from the
//   workbook have real dates. The quarter is the only grain both halves can
//   answer, so it is the grain of every series here — and a monthly view would
//   silently exclude everything before 2021.
//
// VOLUME AND COUNT GET TWO CHARTS, NOT TWO AXES.
//
//   They answer different questions — "how much money moved" and "how many
//   deals happened" — on scales eight orders of magnitude apart. On one pair of
//   axes the count line is a flat smear along the bottom and any crossing point
//   between them is an artefact of the scaling, not a fact about the market.
//   Two charts, stacked, sharing an x-axis.
//
// VOLUME IS ONLY THE DEALS THAT PUBLISHED A FIGURE.
//
//   Around a fifth of the older rows say "Undisclosed". A volume line that
//   quietly drops them looks like a market that contracted, so the share of
//   deals carrying an amount is shown beside it and quarters with poor coverage
//   are called out. The count line has no such problem, which is a large part
//   of why it is here.
//
// COLOUR
//
//   Only one comparison on this page is categorical: bank against non-bank. It
//   is two colours — deepened steps of the brand cyan and amber, validated
//   against the chart surface for lightness, chroma, CVD separation and
//   contrast in both light and dark mode (worst adjacent pair ΔE 18.7 protan).
//   Deals whose lender has never been typed are not a third category; they are
//   an absence, drawn in neutral grey with a hatch so they read as missing
//   rather than as a rival to the other two.
//
//   Everything else — volume, count, sector mix — is a single series and takes
//   one hue. A single series needs no legend; the title names it.
// ───────────────────────────────────────────────────────────────────────────

import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { B, INK_MUTED, INK_PRIMARY, SURFACE, SURFACE_MUTED,
         HAIRLINE, HAIRLINE_FIRM } from "../../../shared/theme";
import { Card, Spinner, ErrorMsg, Btn, Modal, FInput, FSelect, useEscape }
  from "../../../shared/ui";
import { exportCSV } from "../../../shared/format";
import { useApp } from "../../../shared/appContext";
import { useChartTheme, ChartThemeToggle, chartCardStyle } from "../../../shared/chartTheme";

// ── validated palette ──────────────────────────────────────────────────────
// Both pairs pass all five checks of the palette validator against their own
// surface. The dark steps are chosen for dark mode, not derived from the light
// ones — an automatic lightening lands outside the dark lightness band.
const BANK      = { light: "#0e93b1", dark: "#1a9fbd" };
const NONBANK   = { light: "#c07f00", dark: "#c48512" };
const UNTYPED   = { light: "#9aa3ac", dark: "#6b7480" };
const SOLO      = BANK;                    // single-series charts

const num = v => { const n = parseFloat(v); return isFinite(n) ? n : 0; };

// Money on this page is full dollars and routinely nine figures. Rendered at
// the scale a reader thinks in — $1.6B, $87MM — because an axis of
// "1600000000" is a wall of zeros nobody counts.
const money = v => {
  const n = num(v), a = Math.abs(n);
  if (a >= 1e9) return `$${(n / 1e9).toFixed(a >= 1e10 ? 0 : 1)}B`;
  if (a >= 1e6) return `$${(n / 1e6).toFixed(a >= 1e8 ? 0 : 1)}MM`;
  if (a >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};
const moneyFull = v => (v == null ? "—"
  : "$" + Math.round(num(v)).toLocaleString("en-US"));
const pct = v => (v == null ? "—" : `${(num(v) * 100).toFixed(0)}%`);

// A signed percentage, for the change line. The sign is explicit on the upside
// too: "12%" beside "-8%" reads as a smaller number rather than an opposite
// one, and half the point of the pane is which direction the period went.
const signedPct = v => (v == null ? "—"
  : `${v > 0 ? "+" : ""}${(num(v) * 100).toFixed(Math.abs(v) < 0.1 ? 1 : 0)}%`);

// "2026-05" is the key; "May 26" is what a person reads off an axis. Kept apart
// so the key stays sortable and comparable while the label stays short enough
// for twelve of them to fit across a chart. A bare "2026" is already a label.
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const periodLabel = p => {
  const m = String(p || "").match(/^(\d{4})-(\d{2})$/);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${m[1].slice(2)}` : String(p || "");
};

// The band the lender lends in. Named once, used as the page's opening filter and
// as the first preset, so the two cannot say different things.
const DEFAULT_SIZE = { min: "5", max: "50" };

// WHERE THE PAGE OPENS. Same reasoning as the size band above: the cut that
// would otherwise be retyped every visit is the one the page starts on. The
// history behind it is still one click away — the preset menu's "All dates on
// file" — and the date box shows 2025-01-01 rather than sitting blank, so the
// page is visibly filtered rather than quietly so.
//
// NO UPPER BOUND ON PURPOSE. An end date fixed in the file goes stale the day
// after it is written; leaving it open means "up to whatever is loaded", which
// is what "since the start of 2025" actually means.
const DEFAULT_DATE_FROM = "2025-01-01";

export const IndustryClosingsPage = () => {
  const { api, role } = useApp();
  const canUpload = !!(role && role.isAdmin);

  const [data, setData]     = useState(null);
  const [err, setErr]       = useState("");
  const [busy, setBusy]     = useState(true);
  const [dark, setDark]     = useState(false);

  // filters
  //
  // ONE DATE RANGE, NOT A YEAR RANGE AND A QUARTER LIST. Those two could
  // disagree with each other — "2024 to 2026" plus "Q1" is three separate
  // quarters scattered across three years, which is not a period anybody meant
  // and which no chart on this page could draw honestly. Two dates say exactly
  // one thing, and every cut the old pair could express is still reachable.
  const [dateFrom, setDateFrom] = useState(DEFAULT_DATE_FROM);
  const [dateTo, setDateTo]     = useState("");
  const [rollup, setRollup]     = useState([]);
  const [type, setType]         = useState([]);
  const [competitor, setCompetitor] = useState([]);
  // OPENS ON THE LENDER'S BAND. $5–50MM is the market this page exists to watch, so
  // it is where the page starts rather than something retyped every visit. Both
  // boxes show the numbers, so the page is visibly filtered rather than quietly
  // so — and "Clear" empties them, with the preset menu one click from putting
  // them back.
  const [sizeMin, setSizeMin]   = useState(DEFAULT_SIZE.min);   // in $MM, as typed
  const [sizeMax, setSizeMax]   = useState(DEFAULT_SIZE.max);
  const [search, setSearch]     = useState("");
  const [applied, setApplied]   = useState("");      // the search actually sent

  // views
  const [tab, setTab]           = useState("charts"); // charts | lenders | sectors | rows
  const [showTable, setShowTable] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);
  // The quarter picked by clicking a chart. It narrows the deal list under the
  // charts and nothing else — see useDeals.
  // Month or quarter. A one-year cut has four quarterly points and twelve
  // monthly ones, and four is not a shape.
  const [grain, setGrain] = useState("month");
  const [pickedPeriod, setPickedPeriod] = useState(null);
  // { row, from } — which deal is open in the editor, and which grid it came
  // from, so the saved row is patched back into the one the reader is looking at.
  const [editing, setEditing] = useState(null);
  const [savedNote, setSavedNote] = useState("");
  // The lender league table sorts on the server; its key goes into the request.
  const lenderSorter = useSorter("deals", "desc");
  const [upload, setUpload]     = useState(null);     // the upload panel's state

  // ── WHICH PALETTE, AND WHY NOT prefers-color-scheme ──────────────────────
  //
  // This asked the OPERATING SYSTEM whether to draw dark-mode colours. The
  // operating system does not paint this page. ACE has one surface — white,
  // --color-background-primary, no dark theme behind it — so on any machine set
  // to dark mode every chart here was picking its dark-optimised steps and
  // painting them onto a white card. Those steps are chosen for a dark ground;
  // on white they are the wrong lightness against the wrong surface, and the
  // contrast figures each palette was validated at simply did not apply.
  //
  // So the question is asked of the SURFACE the marks are actually drawn on.
  // Today that is always light and the charts always use the light steps. The
  // day a dark theme lands, this needs no edit — it will already be reading the
  // real background rather than a preference that may not have been honoured.
  const surfaceRef = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined" || !surfaceRef.current) return;
    const read = () => {
      // WALK UP UNTIL SOMETHING ACTUALLY PAINTS. This wrapper has padding and
      // no background of its own, so reading it alone returns rgba(0,0,0,0) —
      // which is not "black", it is "ask my parent". Stopping at the first
      // transparent element would answer the question with the element least
      // likely to know.
      let el = surfaceRef.current;
      while (el) {
        const m = (getComputedStyle(el).backgroundColor || "").match(/rgba?\(([^)]+)\)/);
        if (m) {
          const [r, g, b, a] = m[1].split(",").map(Number);
          if (a === undefined || a > 0) {
            // Rec. 709 luma is enough to answer "is this a dark ground"; the
            // exact contrast maths lives in the palette validation, not here.
            setDark((0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.5);
            return;
          }
        }
        el = el.parentElement;
      }
      setDark(false);                       // nothing painted anywhere: it is white
    };
    read();
    // A tenant theme is applied by rewriting variables on :root after mount, so
    // the first read can precede it.
    const mo = new MutationObserver(read);
    const opts = { attributes: true, attributeFilter: ["style", "class", "data-theme"] };
    mo.observe(document.documentElement, opts);
    if (document.body) mo.observe(document.body, opts);
    return () => mo.disconnect();
  }, []);
  // The Dark / Light chart switch (shared/chartTheme) puts the chart cards on
  // a navy ground, which is exactly the case the dark steps were validated
  // for. Either signal selects them.
  const chartT = useChartTheme();
  const darkC = dark || chartT.dark;
  const hue = c => (darkC ? c.dark : c.light);

  // ── the query ────────────────────────────────────────────────────────────
  const params = useMemo(() => {
    const p = {};
    // Sent only when it is a real date. A half-typed "2025-1" is not a filter
    // yet, and shipping it would either be ignored server-side or — worse —
    // quietly narrow to nothing while the boxes still look mid-edit.
    const iso = v => (/^\d{4}-\d{2}-\d{2}$/.test(String(v || "").trim()) ? v.trim() : null);
    if (iso(dateFrom)) p.dateFrom = iso(dateFrom);
    if (iso(dateTo))   p.dateTo   = iso(dateTo);
    if (rollup.length)     p.rollup     = rollup.join(",");
    if (type.length)       p.type       = type.join(",");
    if (competitor.length) p.competitor = competitor.join(",");
    // Typed in millions, sent in dollars — the column holds dollars and nobody
    // should have to count zeros to filter on $25MM.
    const mm = v => { const n = parseFloat(v); return isFinite(n) && n >= 0 ? n * 1e6 : null; };
    const lo = mm(sizeMin), hi = mm(sizeMax);
    if (lo !== null) p.minAmount = lo;
    if (hi !== null) p.maxAmount = hi;
    if (applied)     p.search    = applied;
    p.lenderSort = lenderSorter.key;
    p.lenderDir  = lenderSorter.dir;
    p.grain      = grain;
    return p;
  }, [dateFrom, dateTo, rollup, type, competitor, sizeMin, sizeMax, applied,
      lenderSorter.key, lenderSorter.dir, grain]);
  const key = JSON.stringify(params);

  // Sequenced, so a slow response to an old filter cannot overwrite the answer
  // to the current one. Filters here change fast — a stale write would show
  // numbers that belong to a selection no longer on screen.
  const seq = useRef(0);
  const load = useCallback(() => {
    const mine = ++seq.current;
    setBusy(true); setErr("");
    return api.get("getIndustryClosings", params)
      .then(d => { if (mine === seq.current) { setData(d); setBusy(false); } })
      .catch(e => { if (mine === seq.current) { setErr(e.message); setBusy(false); } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, key]);
  useEffect(() => { load(); }, [load]);

  // The refresh that runs AFTER a write. Deliberately not `load`: load catches
  // its own failure into the page-level error banner, which is exactly what
  // must not happen here — a stale chart following a SUCCESSFUL import is not
  // an error about the import, and showing it as one invites re-importing rows
  // that are already in. This one lets the rejection through so the upload
  // panel can say the write landed and only the figures are behind.
  const refreshAfterWrite = useCallback(() => {
    const mine = ++seq.current;
    setBusy(true);
    return api.get("getIndustryClosings", params)
      .then(d => { if (mine === seq.current) { setData(d); setBusy(false); } })
      .catch(e => { setBusy(false); throw e; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, key]);

  // TWO GRIDS, ONE HOOK. The list under the charts follows whichever quarter is
  // selected; the Deals tab shows everything the filters allow. Each fetches
  // only while its own view is on screen.
  // ONE deal grid now. It lives under the charts, so the Deals tab was two
  // routes to the same list — and the tab version could not be narrowed by
  // clicking a period, which made it the worse of the two.
  const periodDeals = useDeals(api, params, pickedPeriod, tab === "charts");

  const f  = (data && data.filters) || { rollups: [], types: [], competitors: [], years: [] };
  const t  = (data && data.totals)  || { deals: 0, volume: 0, priced: 0, lenders: 0, borrowers: 0 };
  const cov = (data && data.coverage) || {};

  // ── A QUARTER WITH NO DATA IS NOT A QUARTER THAT DID NOT HAPPEN ──────────
  //
  // The server returns only quarters that have rows. Plotted straight, a
  // missing quarter simply is not there — and a line chart draws a clean
  // segment from the quarter before it to the quarter after, which reads as a
  // smooth trend across a hole. There IS such a hole: the workbook's
  // "Q4 - 2025" tab has junk headers and cannot be imported, so unless that tab
  // is fixed the series jumps 2025Q3 to 2026Q1 with nothing to show for it.
  //
  // So the sequence is completed here and the gaps are marked. The line breaks
  // at them, the bars leave the slot empty, and they are named in a note.
  //
  // THE CHANGE IS COMPUTED ON THE COMPLETED SEQUENCE, NOT ON THE ROWS THAT CAME
  // BACK. "Month over month" from a list with March missing would compare April
  // against February and label it MoM, which is a different number wearing the
  // right name. Filling the sequence first makes "the period before this one"
  // mean the calendar period, and a period with nothing in it correctly yields
  // no change rather than a jump.
  const bqAll = useMemo(() => {
    const src = (data && data.byQuarter) || [];
    const g = (data && data.grain) || grain;
    if (!src.length) return [];
    const per = g === "month" ? 12 : g === "quarter" ? 4 : 1;
    const unit = r => (g === "month" ? r.month : g === "quarter" ? r.quarter : 1);
    const keyOfRow = r => r.year * 100 + unit(r);
    const idx2 = new Map(src.map(r => [keyOfRow(r), r]));
    const blank = (y, u) => ({
      period: g === "month" ? `${y}-${String(u).padStart(2, "0")}`
            : g === "year"  ? `${y}` : `${y}Q${u}`,
      year: y, quarter: g === "month" ? Math.ceil(u / 3) : g === "year" ? null : u,
      month: g === "month" ? u : null, missing: true, leadIn: false,
      deals: null, volume: null, priced: null, banks: 0, nonbanks: 0, untyped: 0,
      bankVolume: 0, nonbankVolume: 0, untypedVolume: 0, competitors: 0 });

    const out = [];
    let y = src[0].year, u = unit(src[0]);
    const lastKey = keyOfRow(src[src.length - 1]);
    for (let guard = 0; guard < 900; guard++) {
      const k = y * 100 + u;
      out.push(idx2.get(k) || blank(y, u));
      if (k === lastKey) break;
      u += 1; if (u > per) { u = 1; y += 1; }
    }

    // A change needs a predecessor with something in it. From nothing to
    // something is not "up 100%" and not "up infinity" — it is a start, and it
    // gets no point on the line rather than a number that cannot be read.
    return out.map((r, i) => {
      const prev = i > 0 ? out[i - 1] : null;
      const from = prev && !prev.missing ? num(prev.volume) : null;
      const to   = r.missing ? null : num(r.volume);
      return { ...r,
        prevVolume: from,
        change: (from && to !== null) ? (to - from) / from : null,
        changeFromZero: !!(prev && !prev.missing && !from && to) };
    });
  }, [data, grain]);

  // What the reader asked for. The lead-in period exists to give the first bar
  // a number to change against and is not part of the range.
  const bq = useMemo(() => bqAll.filter(r => !r.leadIn), [bqAll]);
  const gaps = bq.filter(r => r.missing).map(r => r.period);

  // Switching grain invalidates the selection: "2026Q2" is not a point on a
  // monthly axis, and leaving it set would filter the list to a period no
  // longer visible on any chart.
  useEffect(() => { setPickedPeriod(null); }, [grain]);

  const clearAll = () => {
    setDateFrom(""); setDateTo(""); setRollup([]);
    setType([]); setCompetitor([]); setSizeMin(""); setSizeMax("");
    setSearch(""); setApplied("");
  };
  const anyFilter = dateFrom || dateTo || rollup.length
                  || type.length || competitor.length || sizeMin || sizeMax || applied;

  // What the change line is called at this grain. One chart, three cuts — the
  // label has to move with the toggle or the reader is looking at QoQ under a
  // heading that says MoM.
  const CHANGE_LABEL = { month: "MoM", quarter: "QoQ", year: "YoY" }[grain] || "QoQ";

  return (
    <div ref={surfaceRef} style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h2 style={{ margin: 0, color: INK_PRIMARY }}>Industry Closings</h2>
        <span style={{ color: INK_MUTED, fontSize: 13 }}>
          Published deal announcements — the market, not our book.
        </span>
        {/* Loading sits with the page title, not among the filters. It acts on
            the whole table rather than on the current selection, and a control
            inside the filter card reads as one of the filters. */}
        {canUpload ? (
          <span style={{ marginLeft: "auto" }}>
            <UploadControl api={api} state={upload} setState={setUpload} />
          </span>
        ) : null}
      </div>

      {/* Tabs, units and preview open in their own card under the title, so the
          filter row stays a filter row. */}
      {canUpload && upload && upload.filedata ? (
        <Card style={{ marginTop: 12 }}>
          <UploadFlow api={api} state={upload} setState={setUpload}
                      onCommitted={() => { setPickedPeriod(null); return refreshAfterWrite(); }} />
        </Card>
      ) : null}

      {err ? <ErrorMsg msg={err} /> : null}

      {/* ── filters, one row above the charts ───────────────────────────── */}
      <Card style={{ marginTop: 12, padding: 12 }}>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
          <DateRange from={dateFrom} to={dateTo} setFrom={setDateFrom} setTo={setDateTo}
                     min={f.minDate} max={f.maxDate} />
          <Multi label="Sector" value={rollup} setValue={setRollup} options={f.rollups}
                 allLabel="All sectors" />
          <Multi label="Lender type" value={type} setValue={setType} options={f.types}
                 allLabel="All types" />
          <Multi label="Competitor" value={competitor} setValue={setCompetitor}
                 allLabel="All lenders" options={f.competitors}
                 render={v => (v === "Yes" ? "Competitors only" : "Non-competitors only")} />
          <SizeRange min={sizeMin} max={sizeMax} setMin={setSizeMin} setMax={setSizeMax} />
          <form onSubmit={e => { e.preventDefault(); setApplied(search.trim()); }}
                style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <Field label="Search">
              <input value={search} onChange={e => setSearch(e.target.value)}
                     placeholder="Search borrowers, lenders, industries…"
                     style={{ ...inputStyle, minWidth: 230 }} />
            </Field>
            <Btn>Search</Btn>
          </form>
          {anyFilter ? <Btn onClick={clearAll} variant="default">Clear</Btn> : null}
        </div>
      </Card>

      {/* ── the headline ───────────────────────────────────────────────── */}
      <div style={{ display: "grid", gap: 12, marginTop: 12,
                    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
        <Stat label="Closings" value={t.deals.toLocaleString("en-US")}
              note={t.firstyear ? `${t.firstyear}–${t.lastyear}` : null} />
        <Stat label="Volume" value={money(t.volume)}
              note={`${pct(cov.pricedShare)} of deals published a figure`} />
        <Stat label="Lenders" value={t.lenders.toLocaleString("en-US")} />
        <Stat label="Borrowers" value={t.borrowers.toLocaleString("en-US")} />
      </div>

      {/* DID THE API ACTUALLY HONOUR THE SORT?
          A header click refetches, so something visibly happens either way —
          and if the running API predates the sort parameters it ignores them,
          returns the same order, and the page looks broken with nothing to say
          why. The endpoint echoes back the sort it applied; when that is
          missing, or is not what was asked for, this says so. Cheap, and it
          turns "sorting does not work" into a deployment fact. */}
      {data && (!data.lenderSort
                || data.lenderSort.sort !== lenderSorter.key
                || data.lenderSort.dir !== lenderSorter.dir) ? (
        <Note>
          The API returned the lender table in
          {" "}{data.lenderSort
                 ? `${data.lenderSort.sort} ${data.lenderSort.dir} order`
                 : "its own order"}, not the {lenderSorter.key} {lenderSorter.dir} order
          {" "}this page asked for. The running <code>getIndustryClosings</code> is
          probably an older build — redeploy the API, or restart the local
          Functions host, and sorting will work.
        </Note>
      ) : null}

      {/* A MONTH VIEW CANNOT PLACE A ROW WITH NO DATE. Those rows have a
          quarter and nothing finer, so the monthly totals are lower than the
          quarterly ones by exactly this many — a discrepancy worth naming
          before it is noticed. */}
      {grain === "month" && cov.undatedInPeriod ? (
        <Note>
          {cov.undatedInPeriod.toLocaleString("en-US")} closing(s) have a quarter but
          no date, so they cannot be placed in a month and are not in this view.
          Switch to Quarter to include them.
        </Note>
      ) : null}

      {/* WHAT THE DATE RANGE DROPPED. Same shape as the size note below and
          for the same reason: a bound on DATE cannot be true of a row that has
          no date, so those rows leave the selection entirely rather than being
          judged by it. Some migrated closings carry a quarter and nothing
          finer, which is exactly the population this catches. */}
      {cov.dateFiltered && cov.excludedNoDate ? (
        <Note>
          {cov.excludedNoDate.toLocaleString("en-US")} closing(s) match your other
          filters but have no closing date on file, so a date range cannot include
          them. Clear the dates to see them.
        </Note>
      ) : null}

      {/* WHAT THE SIZE FILTER DROPPED. A bound on AMOUNT cannot be true of a
          deal that never published one, so those rows leave the selection
          entirely — and on the older quarters that is a fifth of the book.
          Said out loud, because otherwise the count just gets smaller. */}
      {cov.sizeFiltered && cov.excludedNoAmount ? (
        <Note>
          {cov.excludedNoAmount.toLocaleString("en-US")} closing(s) match your other
          filters but never published an amount, so a size range cannot include them.
          They are not in any figure on this page while a size filter is set.
        </Note>
      ) : null}

      {savedNote ? (
        <Note>
          {savedNote}{" "}
          <button type="button" onClick={() => setSavedNote("")}
                  style={{ border: "none", background: "transparent", cursor: "pointer",
                           color: B.cyan, fontSize: 12 }}>dismiss</button>
        </Note>
      ) : null}

      {/* Coverage, stated rather than left to be inferred from a dipping line. */}
      {cov.worstQuarters && cov.worstQuarters.length ? (
        <Note>
          Volume understates {cov.worstQuarters.map(w => w.period).join(", ")}
          {" "}— fewer than 85% of those closings published an amount
          {" "}({cov.worstQuarters.map(w => `${w.period}: ${w.priced}/${w.deals}`).join(", ")}).
          The deal count is unaffected.
        </Note>
      ) : null}

      <Tabs value={tab} setValue={setTab} tabs={[
        ["charts", "By period"], ["lenders", "Lenders"],
        ["sectors", "Sectors"],
        // Only an administrator can change the mapping, and the endpoints
        // refuse anyone else — so offering the tab to a viewer is a dead end.
        ...(canUpload ? [["mapping", "Mapping"]] : []),
      ]} />

      {busy && !data ? <Spinner /> : null}

      {tab === "charts" && data ? (
        <>
          <Card style={{ marginTop: 12, ...(showTable ? null : chartCardStyle(chartT)) }}>
            <ChartHead
              T={showTable ? null : chartT}
              title={`Financings by ${grain}`}
              sub={`Volume split Bank against non-bank, the ${CHANGE_LABEL} change `
                 + "against the period before, and the closing count. Three panes on "
                 + "one x-axis rather than a second y-axis: on shared axes, where a "
                 + "line crosses the bars is set by the scaling and not by the data."}
              right={
                <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  {showTable ? null : <ChartThemeToggle />}
                  <GrainToggle value={grain} setValue={setGrain} />
                  <Btn variant="default" onClick={() => setShowTable(x => !x)}>
                    {showTable ? "Chart" : "Table"}</Btn>
                </span>} />
            {showTable
              ? <QuarterTable rows={bq} changeLabel={CHANGE_LABEL} />
              : <PeriodChart rows={bq} changeLabel={CHANGE_LABEL}
                             // THE CHANGE LINE IS INK, NOT A THIRD HUE. Amber
                             // already means "non-bank" two panes up; reusing
                             // it for "this period fell" teaches the reader a
                             // colour that means two things. The palette stays
                             // at two hues and both line panes are ink, each
                             // named on its own plot.
                             colors={{ bank: hue(BANK), nonbank: hue(NONBANK),
                                       untyped: hue(UNTYPED), change: chartT.ink }}
                             selected={pickedPeriod} onSelect={setPickedPeriod} />}
          </Card>

          {/* THE SAME NUMBERS, ASKED WHO RATHER THAN HOW MUCH. A stack answers
              the size of the period; it cannot answer the mix, because a period
              twice as large makes both segments twice as tall whatever the mix
              did. That is the whole reason this is its own card and not another
              pane on the chart above. */}
          <Card style={{ marginTop: 12, ...chartCardStyle(chartT) }}>
            <ChartHead title="Bank share of volume" T={chartT}
                       sub={`What share of each ${grain}'s disclosed volume banks wrote.`}
                       right={<span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                         <ChartThemeToggle />
                         <Btn variant="default" onClick={() => setShowMatrix(x => !x)}>
                         {showMatrix ? "Hide table" : "Table"}</Btn></span>} />
            <ShareBand rows={bq} colors={{ bank: hue(BANK), nonbank: hue(NONBANK),
                                           untyped: hue(UNTYPED) }}
                       selected={pickedPeriod} onSelect={setPickedPeriod} />
            <Legend T={chartT} items={[
              { label: "Bank", color: hue(BANK) },
              { label: "Non bank", color: hue(NONBANK) },
              { label: "Lender not typed", color: hue(UNTYPED), hatch: true },
            ]} />
            {/* A table stays on a white ground even in the dark card — it is
                for reading figures off, not for presenting. */}
            {showMatrix
              ? <div style={chartT.dark ? { marginTop: 10, padding: 8, borderRadius: 8,
                                            background: SURFACE } : null}>
                  <TypeMatrix rows={bq} colors={{ bank: BANK.light, nonbank: NONBANK.light }} />
                </div>
              : null}
          </Card>

          {/* THE DEALS BEHIND THE BARS. A quarterly chart answers "how much"
              and immediately raises "which ones" — so the answer sits directly
              under it rather than on another tab. Clicking a quarter narrows
              this list and leaves the charts alone. */}
          <Card style={{ marginTop: 12 }}>
            <DealsPanel
              deals={periodDeals}
              title={pickedPeriod ? `Deals in ${periodLabel(pickedPeriod)}` : "Deals"}
              sub={pickedPeriod ? "" : "Click a period on either chart above to narrow this."}
              // "Nothing matches these filters" is wrong for a quarter that has
              // no rows at all — the filters are not what emptied it.
              emptyMsg={pickedPeriod && gaps.includes(pickedPeriod)
                ? `No closings are on file for ${periodLabel(pickedPeriod)} at all.`
                : undefined}
              onEdit={canUpload ? (r => setEditing({ row: r, from: periodDeals })) : null}
              right={pickedPeriod ? (
                <Btn variant="default" onClick={() => setPickedPeriod(null)}>
                  Showing {periodLabel(pickedPeriod)} · show all
                </Btn>
              ) : null} />
          </Card>
        </>
      ) : null}

      {tab === "lenders" && data ? (
        <Card style={{ marginTop: 12 }}>
          <ChartHead title="Most active lenders"
                     sub={`Top ${(data.byLender || []).length} by number of closings.`}
                     right={<Btn variant="default" onClick={() => exportCSV(
                        data.byLender,
                        ["lender", "type", "competitor", "deals", "volume", "avgDeal", "lastSeen"],
                        "industry-lenders.csv")}>Export</Btn>} />
          <LenderTable rows={data.byLender || []} color={hue(SOLO)}
                       sorter={lenderSorter} />
        </Card>
      ) : null}

      {tab === "sectors" && data ? (
        <>
          {/* AREA ANSWERS "HOW MUCH OF THE MARKET", THE TABLE ANSWERS "WHICH
              AND HOW MUCH". Both, because area is the only honest way to show
              part-to-whole across thirty categories and the worst possible way
              to rank them or read a number off. */}
          <Card style={{ marginTop: 12, ...chartCardStyle(chartT) }}>
            <ChartHead title="Sector mix" T={chartT} right={<ChartThemeToggle />}
                       sub={"Tile size is disclosed volume; colour is how much of that "
                          + "volume banks wrote. Sorted figures are in the table below — "
                          + "area shows the shape of the market, not the order of it."} />
            <SectorTreemap rows={data.byRollup || []} dark={darkC}
                           untypedColor={hue(UNTYPED)}
                           onOpenMapping={canUpload ? (() => setTab("mapping")) : null} />
          </Card>
          <Card style={{ marginTop: 12 }}>
            <ChartHead title="Sectors, ranked"
                       sub="By industry rollup. Deals with no rollup yet are shown as unclassified." />
            <SectorTable rows={data.byRollup || []} color={hue(SOLO)} />
          </Card>
        </>
      ) : null}

      {tab === "mapping" && canUpload ? (
        <MappingPanel api={api} onSaved={() => load()} />
      ) : null}

      {/* THE EDITOR WAS NEVER RENDERED. DealsPanel called setEditing, the state
          was held, and nothing read it — so the edit button set a value and the
          screen did not change. A modal that is built, wired to a save endpoint
          and then never mounted fails completely silently: no error, no console
          entry, just a button that does nothing.

          The vocabulary comes from data.filters, which this page already has —
          the same lists behind the Sector, Lender type and Competitor filters.
          Not from getClosingsMapping: that endpoint is admin-only, and pulling
          it here would make the editor 403 for anyone who can edit a deal but
          not the mapping. */}
      {editing ? (
        <DealEditor
          api={api}
          row={editing.row}
          vocab={{ rollups: f.rollups, types: f.types, competitors: f.competitors }}
          onClose={() => setEditing(null)}
          onSaved={saved => {
            // Patch the row back into the grid it came from, so the change is
            // visible where it was made. If the response came back without a
            // usable row, fall back to a full reload rather than closing over
            // a grid that still shows the old value.
            const ok = editing.from && editing.from.patch && editing.from.patch(saved);
            if (!ok) load();
            setEditing(null);
          }} />
      ) : null}
    </div>
  );
};

// ═══ mapping ══════════════════════════════════════════════════════════════
//
// The other half of the upload's bargain. The importer leaves LENDER TRIM,
// TYPE, COMPETITOR and IndustryRollup blank on anything it has not seen,
// because guessing at them is how a lender ends up permanently mis-typed. This
// is where the blanks get filled — once, in bulk, with the answers written back
// onto the rows.
//
// WRITING TO THE ROWS *IS* TEACHING THE LOOKUP. The enrichment that runs on
// every future upload is built from IndustryClosings itself, so there is no
// separate mapping table to keep in step. Answer "Provider/Rehab Services →
// Healthcare" here and every future upload fills it in unprompted.
//
// SUGGESTIONS COME FROM YOUR OWN HISTORY. An industry gets one only when
// another string with the same sector tail is already mapped — "Manufacturer/
// Medical Devices" can be suggested Healthcare because "Provider/Medical
// Devices" was filed that way. They are filled into the boxes for review, never
// applied on their own: "Accept suggestions" writes them into the form, and you
// still have to press Apply.
//
// BLANKS ONLY. Applying never overwrites a value that is already there, so a
// pass through this screen cannot quietly rewrite years of judgement.
// WHO WAS LENT THE MONEY. An industry string on its own is often not enough to
// map — "Provider/Other" or "Holding Company" describes nothing. The borrowers
// behind it are researchable: you can look a company up and then decide.
//
// Three names inline, the rest one click away. Expanded rather than tooltipped
// because the point is to COPY a name into a search, and nobody can select text
// out of a title attribute.
//
// Most recent first. A name from this quarter still exists under that spelling;
// one from six years ago may not.
const Borrowers = ({ list, total, atLeast }) => {
  const [open, setOpen] = useState(false);
  useEscape(open ? () => setOpen(false) : null);
  if (!list || !list.length) {
    return <span style={{ fontSize: 11, color: INK_MUTED }}>—</span>;
  }
  const shown = open ? list : list.slice(0, 3);
  // total counts every distinct borrower behind the value; list is capped. The
  // hidden number is therefore total - list.length, not total - shown.length,
  // once expanded — say "and N more not listed" rather than pretend otherwise.
  const beyond = Math.max(0, (total || list.length) - list.length);
  return (
    <span style={{ fontSize: 11, lineHeight: 1.5 }}>
      {shown.map((b, i) => (
        <span key={b.name}>
          {i ? ", " : ""}
          <span title={b.deals > 1 ? `${b.deals} deals` : "1 deal"}>{b.name}</span>
        </span>
      ))}
      {list.length > 3 && !open ? (
        <button onClick={() => setOpen(true)}
                style={{ border: "none", background: "transparent", cursor: "pointer",
                         color: B.cyanDark, fontSize: 11, padding: "0 0 0 4px" }}>
          +{list.length - 3} more
        </button>
      ) : null}
      {open && beyond ? (
        <span style={{ color: INK_MUTED }}>
          {" "}— and {atLeast ? "at least " : ""}{beyond} more not listed
        </span>
      ) : null}
    </span>
  );
};

const MappingPanel = ({ api, onSaved }) => {
  const [d, setD]         = useState(null);
  const [busy, setBusy]   = useState(true);
  const [err, setErr]     = useState("");
  const [which, setWhich] = useState("industries");
  const [ind, setInd]     = useState({});      // value -> rollup
  const [len, setLen]     = useState({});      // value -> {trim,type,competitor}
  const [pv, setPv]       = useState(null);    // the preview of what Apply does
  const [note, setNote]   = useState("");
  // CLIENT-SORTED: the endpoint returns the whole list (capped at 300, and
  // there are fewer than that of each), so the browser holds every row and a
  // header click is a comparison rather than a request.
  const indSorter  = useSorter("deals", "desc");
  const lendSorter = useSorter("deals", "desc");

  const load = useCallback(() => {
    setBusy(true); setErr(""); setPv(null);
    api.get("getClosingsMapping")
      .then(x => { setD(x); setBusy(false); })
      .catch(e => { setErr(e.message || String(e)); setBusy(false); });
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const industries = (d && d.industries) || [];
  const lenders    = (d && d.lenders) || [];
  const vocab      = (d && d.vocabulary) || { rollups: [], types: [], competitors: [] };
  const totals     = (d && d.totals) || {};

  // What Apply would send: only the entries actually filled in.
  const items = which === "industries"
    ? industries
        .filter(r => (ind[r.value] || "").trim())
        .map(r => ({ value: r.value, matches: r.matches, rollup: ind[r.value].trim() }))
    : lenders
        .map(r => ({ value: r.value, matches: r.matches, ...(len[r.value] || {}) }))
        .filter(r => (r.trim || "").trim() || (r.type || "").trim() || (r.competitor || "").trim());

  const acceptSuggestions = () => {
    if (which === "industries") {
      const next = { ...ind };
      industries.forEach(r => { if (r.suggestion && !next[r.value]) next[r.value] = r.suggestion; });
      setInd(next);
      setNote(`Filled ${industries.filter(r => r.suggestion).length} suggestion(s) into the form. `
            + `Nothing is saved until you press Apply.`);
    } else {
      const next = { ...len };
      lenders.forEach(r => {
        if (!r.suggestion) return;
        const cur = next[r.value] || {};
        next[r.value] = {
          trim:       cur.trim       || (!r.trim       ? r.suggestion.trim       || "" : ""),
          type:       cur.type       || (!r.type       ? r.suggestion.type       || "" : ""),
          competitor: cur.competitor || (!r.competitor ? r.suggestion.competitor || "" : ""),
        };
      });
      setLen(next);
      setNote(`Filled ${lenders.filter(r => r.suggestion).length} suggestion(s) into the form. `
            + `Nothing is saved until you press Apply.`);
    }
  };

  const kind = which === "industries" ? "industry" : "lender";
  const send = (confirm) => {
    setBusy(true); setErr(""); setNote("");
    api.post("saveClosingsMapping", { kind, items, confirm })
      .then(r => {
        setBusy(false);
        if (confirm) {
          setPv(null); setNote(r.message);
          if (which === "industries") setInd({}); else setLen({});
          load(); onSaved();
        } else setPv(r);
      })
      .catch(e => { setErr(e.message || String(e)); setBusy(false); });
  };

  return (
    <Card style={{ marginTop: 12 }}>
      <ChartHead
        title="Unmapped industries and lenders"
        sub={"Filled in once here, then applied automatically to every future upload — "
           + "the enrichment reads these same rows."}
        right={<Btn variant="default" onClick={load} disabled={busy}>Refresh</Btn>} />

      {err ? <ErrorMsg msg={err} /> : null}
      {busy && !d ? <Spinner /> : null}

      <div style={{ display: "flex", gap: 4, margin: "8px 0 12px",
                    borderBottom: `1px solid ${HAIRLINE}` }}>
        {[["industries", `Industries (${totals.unmappedIndustries || 0})`],
          ["lenders", `Lenders (${totals.unmappedLenders || 0})`]].map(([k, label]) => (
          <button key={k} onClick={() => { setWhich(k); setPv(null); setNote(""); }}
                  style={{ border: "none", background: "transparent", cursor: "pointer",
                           padding: "6px 10px", fontSize: 13,
                           color: which === k ? INK_PRIMARY : INK_MUTED,
                           fontWeight: which === k ? 600 : 400,
                           borderBottom: `2px solid ${which === k ? B.cyan : "transparent"}` }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap",
                    marginBottom: 10 }}>
        <Btn variant="default" onClick={acceptSuggestions} disabled={busy}>
          Accept suggestions
        </Btn>
        <Btn variant="primary" onClick={() => send(false)} disabled={busy || !items.length}>
          Review {items.length} change{items.length === 1 ? "" : "s"}
        </Btn>
        {which === "industries"
          ? (Object.keys(ind).length
              ? <Btn variant="default" onClick={() => { setInd({}); setPv(null); }}>Clear</Btn> : null)
          : (Object.keys(len).length
              ? <Btn variant="default" onClick={() => { setLen({}); setPv(null); }}>Clear</Btn> : null)}
        {busy ? <Spinner /> : null}
      </div>

      {note ? <Note>{note}</Note> : null}

      {/* Preview before write, the same bargain the upload makes. */}
      {pv ? (
        <Note>
          <div><strong>{pv.message}</strong></div>
          <div style={{ marginTop: 6, maxHeight: 160, overflowY: "auto" }}>
            {(pv.detail || []).map(x => (
              <div key={x.value} style={{ fontSize: 12, color: INK_MUTED }}>
                {x.value} → {Object.entries(x.sets).map(([k, v]) => `${k}: ${v}`).join(", ")}
                {" "}· {x.rows} row{x.rows === 1 ? "" : "s"}
                {x.spellings > 1 ? ` · ${x.spellings} spellings` : ""}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 8 }}>
            <Btn variant="primary" onClick={() => send(true)} disabled={busy || !pv.rows}>
              Apply to {pv.rows} row{pv.rows === 1 ? "" : "s"}
            </Btn>
          </div>
        </Note>
      ) : null}

      {/* The vocabulary, offered as a datalist rather than a free-text box, so
          the same rollup does not arrive as three spellings. Typing a genuinely
          new one is still allowed — the list narrows, it does not constrain. */}
      <datalist id="ic-rollups">
        {vocab.rollups.map(r => <option key={r.value} value={r.value} />)}
      </datalist>
      <datalist id="ic-types">
        {vocab.types.map(v => <option key={v} value={v} />)}
      </datalist>
      <datalist id="ic-comps">
        {vocab.competitors.map(v => <option key={v} value={v} />)}
      </datalist>

      {/* Twice the old 520, with a viewport guard. The Apply buttons sit ABOVE
          this list, so a pane taller than the window scrolls them off the top
          and the reader is deep in rows with no way to act on them. 78vh keeps
          the controls on screen on a laptop and still shows roughly twice the
          rows it used to. */}
      <div style={{ overflowX: "auto", maxHeight: "min(1040px, 78vh)", overflowY: "auto" }}>
        {which === "industries" ? (
          <table style={tableStyle}>
            <thead><tr>
              <SortTh id="value" sorter={indSorter}>Industry</SortTh>
              <SortTh id="deals" sorter={indSorter} align="right" numeric>Deals</SortTh>
              {/* Sorting on "Rollup" ranks by WHAT YOU HAVE TYPED, so the rows
                  you have filled in group together and the blanks fall to the
                  end — which is how you find what is left. */}
              <SortTh id="rollup" sorter={indSorter}>Rollup</SortTh>
              <SortTh id="suggestion" sorter={indSorter}>Suggested</SortTh>
              <SortTh id="borrowers" sorter={indSorter} align="right" numeric>Cos.</SortTh>
              <Th>Borrowers</Th>
            </tr></thead>
            <tbody>
              {sortBy(industries, indSorter.key, indSorter.dir,
                      (r, k) => (k === "rollup" ? (ind[r.value] || "")
                               : k === "suggestion" ? r.suggestion
                               : k === "borrowers" ? r.borrowerCount
                               : k === "deals" ? r.deals : r.value)).map(r => (
                <tr key={r.value}>
                  <Td>{r.value}</Td>
                  <Td align="right">{r.deals}</Td>
                  <Td>
                    <input list="ic-rollups" value={ind[r.value] || ""}
                           placeholder={r.suggestion || "—"}
                           onChange={e => setInd({ ...ind, [r.value]: e.target.value })}
                           style={{ ...inputStyle, minWidth: 170 }} />
                  </Td>
                  <Td>
                    {r.suggestion ? (
                      <span style={{ fontSize: 11, color: INK_MUTED }}>
                        {r.suggestion} — {r.suggestedBecause}
                      </span>
                    ) : <span style={{ fontSize: 11, color: INK_MUTED }}>no precedent</span>}
                  </Td>
                  <Td align="right">{r.borrowerCount || 0}</Td>
                  <Td><Borrowers list={r.borrowers} total={r.borrowerCount} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table style={tableStyle}>
            <thead><tr>
              <SortTh id="value" sorter={lendSorter}>Lender</SortTh>
              <SortTh id="deals" sorter={lendSorter} align="right" numeric>Deals</SortTh>
              <SortTh id="volume" sorter={lendSorter} align="right" numeric>Volume</SortTh>
              <SortTh id="lastSeen" sorter={lendSorter} numeric>Last seen</SortTh>
              <SortTh id="trim" sorter={lendSorter}>Trimmed name</SortTh>
              <SortTh id="type" sorter={lendSorter}>Type</SortTh>
              <SortTh id="competitor" sorter={lendSorter}>Competitor</SortTh>
              <SortTh id="borrowers" sorter={lendSorter} align="right" numeric>Cos.</SortTh>
              <Th>Borrowers</Th>
              <SortTh id="missing" sorter={lendSorter} numeric>Missing</SortTh>
            </tr></thead>
            <tbody>
              {sortBy(lenders, lendSorter.key, lendSorter.dir, (r, k) => {
                const v = len[r.value] || {};
                // The editable columns sort on the CURRENT state of the form —
                // what is stored, or what has been typed over it — because that
                // is what is on screen. Sorting on the stored value while the
                // screen shows something else is a table that reorders wrongly.
                if (k === "trim")       return r.trim || v.trim || "";
                if (k === "type")       return r.type || v.type || "";
                if (k === "competitor") return r.competitor || v.competitor || "";
                if (k === "missing")    return r.missing.length;
                if (k === "borrowers")  return r.borrowerCount || 0;
                if (k === "volume")     return r.volume;
                if (k === "lastSeen")   return r.lastSeen || "";
                if (k === "deals")      return r.deals;
                return r.value;
              }).map(r => {
                const v = len[r.value] || {};
                const set = (k, val) => setLen({ ...len, [r.value]: { ...v, [k]: val } });
                return (
                  <tr key={r.value}>
                    <Td>
                      {r.value}
                      {r.matches.length > 1 ? (
                        <span style={badge} title={r.matches.join(" · ")}>
                          {r.matches.length} spellings
                        </span>
                      ) : null}
                    </Td>
                    <Td align="right">{r.deals}</Td>
                    <Td align="right">{money(r.volume)}</Td>
                    <Td>{r.lastSeen || "—"}</Td>
                    <Td>{r.trim
                      ? <span style={{ color: INK_MUTED }}>{r.trim}</span>
                      // Placeholder "—", not the lender's own name. A greyed
                      // "Blue Owl Capital" sitting in an empty box reads as a
                      // value that is already set, and the row then looks done
                      // when nothing has been entered — the change counter
                      // disagrees and there is no way to see why.
                      : <input value={v.trim || ""} placeholder="—"
                               onChange={e => set("trim", e.target.value)}
                               style={{ ...inputStyle, minWidth: 150 }} />}</Td>
                    <Td>{r.type
                      ? <span style={{ color: INK_MUTED }}>{r.type}</span>
                      : <input list="ic-types" value={v.type || ""} placeholder="—"
                               onChange={e => set("type", e.target.value)}
                               style={{ ...inputStyle, width: 100 }} />}</Td>
                    <Td>{r.competitor
                      ? <span style={{ color: INK_MUTED }}>{r.competitor}</span>
                      : <input list="ic-comps" value={v.competitor || ""} placeholder="—"
                               onChange={e => set("competitor", e.target.value)}
                               style={{ ...inputStyle, width: 80 }} />}</Td>
                    <Td align="right">{r.borrowerCount || 0}{r.borrowersCapped ? "+" : ""}</Td>
                    <Td><Borrowers list={r.borrowers} total={r.borrowerCount}
                                   atLeast={r.borrowersCapped} /></Td>
                    <Td><span style={{ fontSize: 11, color: INK_MUTED }}>
                      {r.missing.join(", ")}</span></Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* A page of the list, not all of it, when there is more than fits. */}
      {totals.shown && (which === "industries"
          ? totals.unmappedIndustries > totals.shown.industries
          : totals.unmappedLenders > totals.shown.lenders) ? (
        <Note>
          Showing the {which === "industries" ? totals.shown.industries : totals.shown.lenders}
          {" "}most common of {which === "industries" ? totals.unmappedIndustries : totals.unmappedLenders}.
          Apply these and refresh for the next batch — the busiest come first, so
          the early passes cover the most deals.
        </Note>
      ) : null}
    </Card>
  );
};

// ═══ charts ═══════════════════════════════════════════════════════════════

// Right padding is generous on purpose: the last x-axis label and the direct
// value label both sit at the right edge, and at r:18 a "2026Q3" ran off the
// plot. Caught by rendering it, not by reading it.
const PAD = { l: 62, r: 46, t: 12, b: 34 };

// ── VOLUME AND COUNT, ONE CARD, TWO PANES ──────────────────────────────────
//
// Bars for volume, a line for the count, one x-axis, one hover, one selection.
//
// WHY NOT TWO Y-AXES, WHICH IS WHAT A COMBO CHART USUALLY MEANS.
//
//   The two measures are eight orders of magnitude apart, so a shared plot
//   needs two independent scales — and the moment there are two scales, WHERE
//   THE LINE SITS RELATIVE TO THE BARS IS SOMETHING THE CHART DECIDED, not
//   something the data says. Slide either axis and the line crosses the bars
//   somewhere else. Readers take that crossing as meaning "count overtook
//   volume this quarter", and it means nothing at all; it is the single most
//   common way a chart tells a lie its author did not intend.
//
//   Stacking the panes keeps everything the second axis was for — one card,
//   one time axis, the two series read together at a glance, a single hover
//   that answers both — and gives up only the false crossover. Each pane keeps
//   its own scale, visibly its own, because they are separated rather than
//   overlaid.
//
// AND THE QUESTION UNDERNEATH. "Compare volume with count" is almost always
// asking whether deals are getting BIGGER or whether there are simply MORE of
// them. Volume divided by count answers that outright, so the average deal
// size is in the tooltip and in the table rather than left to be eyeballed off
// two shapes.
// PANE_GAP has to clear two axis labels, not just separate two plots: the
// volume axis's "$0" sits at the bottom of the upper pane and the count's
// maximum sits at the top of the lower one. At 10px they printed on top of
// each other.
const MAIN_H = 168, CHG_H = 58, SUB_H = 58, PANE_GAP = 24, AXIS_H = 36;
// Baseline to the top of the tick labels. Measured from the axis line rather
// than up from the bottom of the SVG, because "10px off the bottom edge" is not
// a distance the reader sees — the gap they see is the one between the line and
// the text, and at 8px the labels sat on the axis.
const AXIS_GAP = 15;
const COMBO_H = MAIN_H + PANE_GAP + CHG_H + PANE_GAP + SUB_H + AXIS_H;
// The change pane's axis stops here however far the data goes. One period off a
// near-zero base reads +2,171%, and an axis stretched to fit that draws every
// other period as a flat line. Points past it are marked and labelled, not
// dropped — see PeriodChart.
const CHG_CAP = 2;

const PeriodChart = ({ rows, colors, changeLabel, selected, onSelect }) => {
  const T = useChartTheme();
  const wrap = useRef(null);
  const [w, setW] = useState(720);
  const [hi, setHi] = useState(null);
  useEffect(() => {
    if (!wrap.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(es => setW(Math.max(320, es[0].contentRect.width)));
    ro.observe(wrap.current); return () => ro.disconnect();
  }, []);
  if (!rows.length) return <Empty />;

  const iw = w - PAD.l - PAD.r;
  const slot = iw / rows.length;
  // THE CAP SCALES WITH HOW MANY PERIODS THERE ARE. A fixed 26px maximum is
  // right for a twelve-month cut and absurd for a three-year one, where it
  // leaves three hairlines adrift in a wide card and the eye reads emptiness
  // rather than three large years. Wide bars for few periods, narrow for many.
  const bw = Math.max(3, Math.min(rows.length <= 4 ? 84 : rows.length <= 8 ? 48 : 26,
                                  slot - 4));
  const x = i => PAD.l + (i + 0.5) * slot;

  const vMax = Math.max(...rows.map(r => r.volume || 0), 0) || 1;
  const cMax = Math.max(...rows.map(r => r.deals || 0), 0) || 1;
  const vy = v => PAD.t + MAIN_H - (num(v) / vMax) * MAIN_H;

  const chgTop = PAD.t + MAIN_H + PANE_GAP;
  const subTop = chgTop + CHG_H + PANE_GAP;
  const cy = v => subTop + SUB_H - (num(v) / cMax) * SUB_H;

  // ── THE CHANGE PANE'S SCALE IS CAPPED, AND SAYS SO ────────────────────────
  //
  // One month off a near-zero base produces +2,171%, and an axis stretched to
  // fit it renders every other period as a flat line through the middle — the
  // outlier eats the chart. So the axis fits the data up to CHG_CAP and stops
  // there, and anything past it is drawn as an arrow at the edge WITH ITS
  // VALUE PRINTED. Clipping a point silently would be the actual lie; clipping
  // it visibly is just a scale.
  const chgVals = rows.filter(r => r.change != null).map(r => Math.abs(r.change));
  const chgMax = Math.min(CHG_CAP, Math.max(0.1, niceStep(Math.max(...chgVals, 0.1)) * 2));
  const zeroY = chgTop + CHG_H / 2;
  const chy = v => zeroY - (Math.max(-chgMax, Math.min(chgMax, num(v))) / chgMax) * (CHG_H / 2);

  const vTicks = axisTicks(vMax, 3);
  const stride = Math.max(1, Math.ceil(rows.length / 12));
  const selIdx = selected ? rows.findIndex(r => r.period === selected) : -1;

  // Both lines break at the gaps. A segment drawn across a period with no data
  // is a claim about that period.
  const runsOf = (has) => {
    const runs = [];
    rows.forEach((r, i) => {
      if (!has(r)) { runs.push(null); return; }
      const last = runs[runs.length - 1];
      if (Array.isArray(last)) last.push(i); else runs.push([i]);
    });
    return runs.filter(Array.isArray).filter(seg => seg.length);
  };
  const pathOf = (seg, yOf, key) => seg.map((i, k) =>
    `${k ? "L" : "M"}${x(i).toFixed(1)},${yOf(rows[i][key]).toFixed(1)}`).join(" ");

  const countSegs = runsOf(r => !r.missing && r.deals != null);

  const r0 = hi !== null ? rows[hi] : null;
  const share = (part, whole) => (whole ? part / whole : null);

  return (
    <div ref={wrap} style={{ position: "relative" }}>
      <svg width="100%" height={COMBO_H} viewBox={`0 0 ${w} ${COMBO_H}`} role="img"
           aria-label={`Volume by lender type, ${changeLabel} change, and deal count by period`}
           style={{ cursor: onSelect ? "pointer" : "default" }}
           onMouseLeave={() => setHi(null)}
           onMouseMove={e => {
             const box = e.currentTarget.getBoundingClientRect();
             const px = ((e.clientX - box.left) / box.width) * w;
             const i = Math.floor((px - PAD.l) / slot);
             setHi(i >= 0 && i < rows.length ? i : null);
           }}
           onClick={() => {
             if (!onSelect || hi === null) return;
             onSelect(rows[hi].period === selected ? null : rows[hi].period);
           }}>
        <defs>
          {/* Not a third hue: a hatch, so "we have not typed this lender yet"
              reads as an absence rather than as a rival category. */}
          <pattern id="ic-hatch" width="6" height="6" patternTransform="rotate(45)"
                   patternUnits="userSpaceOnUse">
            <rect width="6" height="6" fill={colors.untyped} opacity="0.25" />
            <line x1="0" y1="0" x2="0" y2="6" stroke={colors.untyped} strokeWidth="2.5" />
          </pattern>
        </defs>

        {/* The selection spans ALL THREE panes — it is one period, not three. */}
        {selIdx !== -1 ? (
          <rect x={PAD.l + selIdx * slot} y={PAD.t} width={slot}
                height={subTop + SUB_H - PAD.t} fill={colors.bank} opacity="0.12" rx="3" />
        ) : null}

        {/* ── pane 1: volume, stacked by lender type ── */}
        {vTicks.map(tv => (
          <g key={"v" + tv}>
            <line x1={PAD.l} x2={w - PAD.r} y1={vy(tv)} y2={vy(tv)}
                  stroke={T.grid} strokeWidth="1" />
            <text x={PAD.l - 8} y={vy(tv) + 4} textAnchor="end"
                  fontSize="11" fill={T.muted}>{money(tv)}</text>
          </g>
        ))}
        {rows.map((r, i) => {
          if (r.missing || !r.volume) return null;
          const segs = [
            { k: "bank",    v: r.bankVolume,    fill: colors.bank },
            { k: "nonbank", v: r.nonbankVolume, fill: colors.nonbank },
            { k: "untyped", v: r.untypedVolume, fill: "url(#ic-hatch)" },
          ].filter(s => num(s.v) > 0);
          const full = MAIN_H - (vy(r.volume) - PAD.t);
          let acc = 0;
          return (
            <g key={"b" + i} opacity={hi === i ? 1 : 0.88}>
              {segs.map((s, si) => {
                const sh = (num(s.v) / num(r.volume)) * full;
                const yTop = PAD.t + MAIN_H - acc - sh;
                acc += sh;
                // A 1px spacer so the boundary between two stacked segments is
                // a line rather than a colour change — the one place a stack
                // is genuinely ambiguous.
                const gap = si < segs.length - 1 ? 1 : 0;
                return <rect key={s.k} x={x(i) - bw / 2} y={yTop + gap} width={bw}
                             height={Math.max(0.5, sh - gap)} fill={s.fill}
                             rx={si === segs.length - 1 ? 3 : 0} />;
              })}
            </g>
          );
        })}
        <line x1={PAD.l} x2={w - PAD.r} y1={PAD.t + MAIN_H} y2={PAD.t + MAIN_H}
              stroke={T.gridFirm} strokeWidth="1" />

        {/* ── pane 2: change against the period before ── */}
        <text x={PAD.l + 2} y={chgTop - 4} fontSize="10" fill={T.muted}>
          {changeLabel} change in volume
        </text>
        <text x={PAD.l - 8} y={chy(chgMax) + 9} textAnchor="end"
              fontSize="11" fill={T.muted}>{signedPct(chgMax)}</text>
        <text x={PAD.l - 8} y={zeroY + 4} textAnchor="end"
              fontSize="11" fill={T.muted}>0</text>
        <text x={PAD.l - 8} y={chy(-chgMax)} textAnchor="end"
              fontSize="11" fill={T.muted}>{signedPct(-chgMax)}</text>
        <line x1={PAD.l} x2={w - PAD.r} y1={zeroY} y2={zeroY}
              stroke={T.gridFirm} strokeWidth="1" />
        {/* COLUMNS FROM ZERO, NOT A LINE. A change is a signed quantity
            against a baseline, and a column says which side of the baseline it
            fell on before the reader has read a single number. A line here also
            looked exactly like the closings line one pane down — same ink, same
            weight, two panes apart — which is a confusion the shape removes.
            The trend is still legible in the envelope of the columns. */}
        {rows.map((r, i) => {
          if (r.change == null) return null;
          const yy = chy(r.change);
          const up = r.change > 0;
          const over = Math.abs(r.change) > chgMax;
          const cw = Math.max(2, Math.min(bw * 0.7, 18));
          return (
            <g key={"hp" + i} opacity={hi === i ? 1 : 0.85}>
              <rect x={x(i) - cw / 2} y={Math.min(yy, zeroY)} width={cw}
                    height={Math.max(1, Math.abs(zeroY - yy))} fill={colors.change} rx="1" />
              {/* Off the end of the capped axis. Marked with an arrowhead AND
                  labelled with the number it could not reach — silently
                  clipping it would be the actual lie. */}
              {over ? (
                <>
                  <path d={`M${x(i) - cw / 2 - 2},${yy + (up ? 4 : -4)} `
                          + `L${x(i) + cw / 2 + 2},${yy + (up ? 4 : -4)} `
                          + `L${x(i)},${yy - (up ? 3 : -3)} Z`}
                        fill={colors.change} />
                  <text x={x(i)} y={yy + (up ? -6 : 14)} textAnchor="middle"
                        fontSize="9" fontWeight="600" fill={colors.change}>
                    {signedPct(r.change)}
                  </text>
                </>
              ) : null}
            </g>
          );
        })}
        {/* Redrawn over the columns so the baseline is never buried by one. */}
        <line x1={PAD.l} x2={w - PAD.r} y1={zeroY} y2={zeroY}
              stroke={T.gridFirm} strokeWidth="1" />

        {/* ── pane 3: how many closings ── */}
        <text x={PAD.l + 2} y={subTop - 4} fontSize="10" fill={T.muted}>closings</text>
        <text x={PAD.l - 8} y={cy(cMax) + 9} textAnchor="end"
              fontSize="11" fill={T.muted}>{Math.round(cMax)}</text>
        <text x={PAD.l - 8} y={subTop + SUB_H + 4} textAnchor="end"
              fontSize="11" fill={T.muted}>0</text>
        <line x1={PAD.l} x2={w - PAD.r} y1={subTop + SUB_H} y2={subTop + SUB_H}
              stroke={T.gridFirm} strokeWidth="1" />
        {countSegs.map((seg, k) => (
          <path key={"c" + k} d={pathOf(seg, cy, "deals")} fill="none" stroke={T.ink}
                strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
        ))}
        {countSegs.filter(seg => seg.length === 1).map(seg => (
          <circle key={"cp" + seg[0]} cx={x(seg[0])} cy={cy(rows[seg[0]].deals)}
                  r="2.5" fill={T.ink} />
        ))}

        {/* gaps, marked in every pane */}
        {rows.map((r, i) => (r.missing ? (
          <line key={"g" + i} x1={x(i)} x2={x(i)} y1={PAD.t + MAIN_H - 6}
                y2={subTop + SUB_H} stroke={T.muted} strokeWidth="1" strokeDasharray="2 2" />
        ) : null))}

        {hi !== null ? (
          <line x1={x(hi)} x2={x(hi)} y1={PAD.t} y2={subTop + SUB_H}
                stroke={T.gridFirm} strokeWidth="1" />
        ) : null}

        {rows.map((r, i) => (i % stride === 0 || i === rows.length - 1 || i === selIdx ? (
          <text key={r.period} x={x(i)} y={subTop + SUB_H + AXIS_GAP}
                textAnchor={i === rows.length - 1 ? "end" : "middle"}
                fontSize="11" fontWeight={i === selIdx ? 600 : 400}
                fill={i === selIdx ? T.ink : T.muted}>{periodLabel(r.period)}</text>
        ) : null))}
      </svg>

      {r0 ? (
        <Tooltip T={T} x={(x(hi) / w) * 100}>
          <strong>{periodLabel(r0.period)}</strong>
          {r0.missing ? <div style={{ color: T.muted }}>no closings on file</div> : (
            <>
              <div>Volume: {money(r0.volume)}</div>
              <div style={{ color: colors.bank }}>
                Bank: {money(r0.bankVolume)} · {pct(share(r0.bankVolume, r0.volume))}
                {" "}· {r0.banks} deal{r0.banks === 1 ? "" : "s"}
              </div>
              <div style={{ color: colors.nonbank }}>
                Non bank: {money(r0.nonbankVolume)} · {pct(share(r0.nonbankVolume, r0.volume))}
                {" "}· {r0.nonbanks} deal{r0.nonbanks === 1 ? "" : "s"}
              </div>
              {r0.untyped ? <div style={{ color: T.muted }}>
                Not typed: {money(r0.untypedVolume)} · {r0.untyped}</div> : null}
              <div>{changeLabel}: {r0.change == null
                ? (r0.changeFromZero ? "— (from nothing)" : "—")
                : signedPct(r0.change)}
                {r0.prevVolume ? <span style={{ color: T.muted }}>
                  {" "}(from {money(r0.prevVolume)})</span> : null}
              </div>
              <div>Closings: {r0.deals}</div>
              {/* The question a volume-against-count comparison is really
                  asking: bigger deals, or simply more of them. */}
              <div>Average: {r0.priced ? money(r0.volume / r0.priced) : "—"}</div>
              {r0.priced < r0.deals ? (
                <div style={{ color: T.muted }}>
                  {r0.priced} of {r0.deals} published a figure
                </div>
              ) : null}
            </>
          )}
        </Tooltip>
      ) : null}

      <Legend T={T} items={[
        { label: "Bank volume", color: colors.bank },
        { label: "Non bank volume", color: colors.nonbank },
        { label: "Lender not typed", color: colors.untyped, hatch: true },
        { label: `${changeLabel} change in volume (middle pane)`, color: colors.change },
        { label: "Closings (lower pane)", color: T.ink },
      ]} />
    </div>
  );
};

// ── BANK SHARE ─────────────────────────────────────────────────────────────
//
// The same numbers as the stack above, asked a different question of. The bars
// answer "how big was the period"; this answers "who wrote it", which the
// stack cannot because a period twice the size makes every segment twice as
// tall whatever the mix did.
//
// FULL-HEIGHT BANDS, NOT A LINE. Two shares that sum to one are one number,
// and a band shows the boundary moving rather than asking the reader to
// subtract from 100. A period with no disclosed volume has no share at all and
// is left blank rather than drawn at zero, which would read as "all non-bank".
const BAND_H = 96;
const ShareBand = ({ rows, colors, selected, onSelect }) => {
  const T = useChartTheme();
  const wrap = useRef(null);
  const [w, setW] = useState(720);
  const [hi, setHi] = useState(null);
  useEffect(() => {
    if (!wrap.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(es => setW(Math.max(320, es[0].contentRect.width)));
    ro.observe(wrap.current); return () => ro.disconnect();
  }, []);
  if (!rows.length) return <Empty />;

  const H2 = BAND_H + AXIS_H;
  const iw = w - PAD.l - PAD.r;
  const slot = iw / rows.length;
  const bw = Math.max(3, Math.min(rows.length <= 4 ? 84 : rows.length <= 8 ? 48 : 30,
                                  slot - 2));
  const x = i => PAD.l + (i + 0.5) * slot;
  const stride = Math.max(1, Math.ceil(rows.length / 12));
  const selIdx = selected ? rows.findIndex(r => r.period === selected) : -1;
  const sh = r => (num(r.volume) ? {
    bank: num(r.bankVolume) / num(r.volume),
    nonbank: num(r.nonbankVolume) / num(r.volume),
    untyped: num(r.untypedVolume) / num(r.volume),
  } : null);
  const r0 = hi !== null ? rows[hi] : null;
  const s0 = r0 ? sh(r0) : null;

  return (
    <div ref={wrap} style={{ position: "relative" }}>
      <svg width="100%" height={H2} viewBox={`0 0 ${w} ${H2}`} role="img"
           aria-label="Bank against non-bank share of volume by period"
           style={{ cursor: onSelect ? "pointer" : "default" }}
           onMouseLeave={() => setHi(null)}
           onMouseMove={e => {
             const box = e.currentTarget.getBoundingClientRect();
             const px = ((e.clientX - box.left) / box.width) * w;
             const i = Math.floor((px - PAD.l) / slot);
             setHi(i >= 0 && i < rows.length ? i : null);
           }}
           onClick={() => { if (onSelect && hi !== null)
             onSelect(rows[hi].period === selected ? null : rows[hi].period); }}>
        {/* Its own hatch id. The pattern in PeriodChart is only in the
            document while that chart is rendered, and this card is visible
            when the other one is switched to its table — a fill pointing at a
            missing pattern paints nothing at all. */}
        <defs>
          <pattern id="ic-hatch-share" width="6" height="6" patternTransform="rotate(45)"
                   patternUnits="userSpaceOnUse">
            <rect width="6" height="6" fill={colors.untyped} opacity="0.25" />
            <line x1="0" y1="0" x2="0" y2="6" stroke={colors.untyped} strokeWidth="2.5" />
          </pattern>
        </defs>
        {[0, 0.25, 0.5, 0.75, 1].map(g => (
          <g key={g}>
            <line x1={PAD.l} x2={w - PAD.r} y1={PAD.t + g * BAND_H} y2={PAD.t + g * BAND_H}
                  stroke={g === 0.5 ? T.gridFirm : T.grid} strokeWidth="1" />
            {g === 0 || g === 0.5 || g === 1 ? (
              <text x={PAD.l - 8} y={PAD.t + g * BAND_H + 4} textAnchor="end"
                    fontSize="11" fill={T.muted}>{Math.round((1 - g) * 100)}%</text>
            ) : null}
          </g>
        ))}
        {selIdx !== -1 ? (
          <rect x={PAD.l + selIdx * slot} y={PAD.t - 3} width={slot} height={BAND_H + 6}
                fill={colors.bank} opacity="0.12" rx="3" />
        ) : null}
        {rows.map((r, i) => {
          const s = sh(r);
          if (!s) return null;
          let acc = 0;
          return (
            <g key={r.period} opacity={hi === i ? 1 : 0.9}>
              {[["bank", colors.bank], ["nonbank", colors.nonbank],
                ["untyped", "url(#ic-hatch-share)"]].map(([k, fill]) => {
                const h2 = s[k] * BAND_H;
                if (h2 <= 0) return null;
                const y = PAD.t + BAND_H - acc - h2;
                acc += h2;
                return <rect key={k} x={x(i) - bw / 2} y={y} width={bw}
                             height={Math.max(0.5, h2)} fill={fill} />;
              })}
            </g>
          );
        })}
        {rows.map((r, i) => (i % stride === 0 || i === rows.length - 1 || i === selIdx ? (
          <text key={r.period} x={x(i)} y={PAD.t + BAND_H + AXIS_GAP}
                textAnchor={i === rows.length - 1 ? "end" : "middle"}
                fontSize="11" fontWeight={i === selIdx ? 600 : 400}
                fill={i === selIdx ? T.ink : T.muted}>{periodLabel(r.period)}</text>
        ) : null))}
      </svg>
      {r0 ? (
        <Tooltip T={T} x={(x(hi) / w) * 100}>
          <strong>{periodLabel(r0.period)}</strong>
          {s0 ? (
            <>
              <div style={{ color: colors.bank }}>Bank: {pct(s0.bank)} · {money(r0.bankVolume)}</div>
              <div style={{ color: colors.nonbank }}>
                Non bank: {pct(s0.nonbank)} · {money(r0.nonbankVolume)}</div>
              {s0.untyped ? <div style={{ color: T.muted }}>
                Not typed: {pct(s0.untyped)}</div> : null}
            </>
          ) : <div style={{ color: T.muted }}>no disclosed volume</div>}
        </Tooltip>
      ) : null}
    </div>
  );
};

// ── THE MATRIX ─────────────────────────────────────────────────────────────
//
// Three blocks — dollars, share, count — by period across the top, which is
// the shape the PowerBI report reads in and the shape you copy out of. It
// scrolls sideways rather than shrinking: twenty periods squeezed into a card
// width is a table nobody can read a number off.
//
// FIGURES IN FULL DOLLARS, ROUNDED TO MILLIONS FOR WIDTH. The label says so
// once rather than each cell carrying a unit.
const TypeMatrix = ({ rows, colors }) => {
  if (!rows.length) return <Empty />;
  const mm = v => (v == null ? "—" : `$${Math.round(num(v) / 1e6).toLocaleString("en-US")}`);
  const sum = k => rows.reduce((s, r) => s + num(r[k]), 0);
  const anyUntyped = rows.some(r => num(r.untypedVolume) > 0 || num(r.untyped) > 0);

  const BLOCKS = [
    { title: "Volume ($MM)", cell: (r, k) => mm(r[k.vol]),
      total: k => mm(sum(k.vol)), grand: r => mm(r.volume), grandAll: () => mm(sum("volume")) },
    { title: "Share of volume",
      cell: (r, k) => (num(r.volume) ? pct(num(r[k.vol]) / num(r.volume)) : "—"),
      total: k => (sum("volume") ? pct(sum(k.vol) / sum("volume")) : "—"),
      grand: () => "100%", grandAll: () => "100%" },
    { title: "Closings", cell: (r, k) => num(r[k.cnt]).toLocaleString("en-US"),
      total: k => sum(k.cnt).toLocaleString("en-US"),
      grand: r => num(r.deals).toLocaleString("en-US"),
      grandAll: () => sum("deals").toLocaleString("en-US") },
  ];
  const KINDS = [
    { label: "Bank", vol: "bankVolume", cnt: "banks", color: colors.bank },
    { label: "Non Bank", vol: "nonbankVolume", cnt: "nonbanks", color: colors.nonbank },
    ...(anyUntyped ? [{ label: "Not typed", vol: "untypedVolume", cnt: "untyped",
                        color: INK_MUTED }] : []),
  ];

  const cellS = { padding: "5px 10px", textAlign: "right", whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums" };
  const headS = { ...cellS, fontSize: 11, fontWeight: 700, color: INK_MUTED,
                  textTransform: "uppercase", letterSpacing: "0.04em",
                  borderBottom: `1px solid ${HAIRLINE_FIRM}` };

  return (
    <div style={{ overflowX: "auto", marginTop: 6 }}>
      {BLOCKS.map((b, bi) => (
        <table key={b.title} style={{ ...tableStyle, marginTop: bi ? 18 : 6 }}>
          <thead>
            <tr>
              <th style={{ ...headS, textAlign: "left", position: "sticky", left: 0,
                           background: SURFACE, minWidth: 96 }}>{b.title}</th>
              {rows.map(r => <th key={r.period} style={headS}>{periodLabel(r.period)}</th>)}
              <th style={{ ...headS, borderLeft: `1px solid ${HAIRLINE_FIRM}` }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {KINDS.map(k => (
              <tr key={k.label}>
                <td style={{ ...cellS, textAlign: "left", position: "sticky", left: 0,
                             background: SURFACE, color: INK_PRIMARY }}>
                  <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2,
                                 background: k.color, marginRight: 6 }} />
                  {k.label}
                </td>
                {rows.map(r => <td key={r.period} style={{ ...cellS, color: INK_PRIMARY }}>
                  {r.missing ? "—" : b.cell(r, k)}</td>)}
                <td style={{ ...cellS, color: INK_PRIMARY, fontWeight: 600,
                             borderLeft: `1px solid ${HAIRLINE_FIRM}` }}>{b.total(k)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...cellS, textAlign: "left", position: "sticky", left: 0,
                           background: SURFACE, fontWeight: 600, color: INK_PRIMARY,
                           borderTop: `1px solid ${HAIRLINE_FIRM}` }}>Total</td>
              {rows.map(r => <td key={r.period}
                style={{ ...cellS, fontWeight: 600, color: INK_PRIMARY,
                         borderTop: `1px solid ${HAIRLINE_FIRM}` }}>
                {r.missing ? "—" : b.grand(r)}</td>)}
              <td style={{ ...cellS, fontWeight: 600, color: INK_PRIMARY,
                           borderTop: `1px solid ${HAIRLINE_FIRM}`,
                           borderLeft: `1px solid ${HAIRLINE_FIRM}` }}>{b.grandAll()}</td>
            </tr>
          </tbody>
        </table>
      ))}
    </div>
  );
};

// ── SECTOR TREEMAP ─────────────────────────────────────────────────────────
//
// AREA IS VOLUME. COLOUR IS BANK SHARE. Those are two different questions asked
// of one picture, and the second is the reason this is a treemap rather than
// the sorted bars underneath it: bars already rank sectors better than area
// ever will — nobody reliably orders the fourth and fifth rectangle by eye —
// but bars cannot show which sectors are bank territory and which are not
// without becoming a second chart.
//
// WHAT A TREEMAP IS ACTUALLY GOOD AT is part-to-whole across many categories:
// "healthcare and manufacturing are most of this market" is one glance. What it
// is bad at is ranking and reading values, so the ranking and the values live
// in the table below and the tiles carry only what area can honestly say.
//
// TWELVE TILES, THEN "OTHER". Past that the rectangles get smaller than their
// own labels — the failure mode where a chart truncates categories to three
// letters and stops being readable at exactly the point it has most ink on the
// page. The remainder is one tile, and it is still a real sector total with a
// real bank share, so it needs no special case.
//
// A SECTOR WITH NO DISCLOSED VOLUME HAS NO AREA. It cannot be drawn, so it is
// counted and named under the chart rather than silently missing.
const TREE_H = 340;

// The diverging scale, both modes, validated rather than eyeballed.
//
// Lightness carries "how far from an even split" and hue carries "which side",
// so the ramp still reads when hue does not — that is the entire construction
// of a diverging scale and the reason the midpoint is neutral.
//
//   light: midpoint lightest, each arm darkens outward
//   dark:  midpoint darkest,  each arm lightens outward (not an auto-flip of
//          the light steps — those land outside the dark lightness band)
//
// Measured: every step's label clears 4.5:1 on its own fill in both modes, the
// two inner steps sit ~18 ΔE apart under protanopia and ~20 under normal
// vision, and the midpoint's chroma is under 0.03 so it reads as "neither".
// Each step carries its own ink, because which ink works is a property of the
// fill and cannot be decided once for the ramp.
const SHARE_SCALE = {
  light: [
    { max: 0.20, fill: "#c07f00", ink: "#1a1a1a", label: "Mostly non-bank" },
    { max: 0.40, fill: "#e8b551", ink: "#1a1a1a", label: "Non-bank leaning" },
    { max: 0.60, fill: "#dfe3e6", ink: "#1a1a1a", label: "Even split" },
    { max: 0.80, fill: "#69c6de", ink: "#1a1a1a", label: "Bank leaning" },
    { max: 1.01, fill: "#0e93b1", ink: "#1a1a1a", label: "Mostly bank" },
  ],
  dark: [
    { max: 0.20, fill: "#c48512", ink: "#1a1a1a", label: "Mostly non-bank" },
    { max: 0.40, fill: "#96660b", ink: "#ffffff", label: "Non-bank leaning" },
    { max: 0.60, fill: "#3a4149", ink: "#ffffff", label: "Even split" },
    { max: 0.80, fill: "#127a94", ink: "#ffffff", label: "Bank leaning" },
    { max: 1.01, fill: "#1a9fbd", ink: "#1a1a1a", label: "Mostly bank" },
  ],
};
const shareStep = (share, dark) =>
  (SHARE_SCALE[dark ? "dark" : "light"]).find(s => share <= s.max)
  || SHARE_SCALE[dark ? "dark" : "light"][4];

// Squarified treemap (Bruls, Huizing & van Wijk). Plain rows would give tiles
// with extreme aspect ratios, and a 3:400 sliver is impossible to point at,
// impossible to label, and impossible to judge the area of.
function squarify(items, x, y, w, h) {
  const out = [];
  const total = items.reduce((a, b) => a + b.v, 0);
  if (!total || w <= 0 || h <= 0) return out;
  const queue = items.map(d => ({ ...d, a: (d.v / total) * w * h }));
  let rect = { x, y, w, h };
  let row = [];

  const worst = (r, len) => {
    const s = r.reduce((a, b) => a + b.a, 0);
    if (!s || !len) return Infinity;
    const mx = Math.max(...r.map(d => d.a)), mn = Math.min(...r.map(d => d.a));
    return Math.max((len * len * mx) / (s * s), (s * s) / (len * len * mn));
  };
  const flush = (r) => {
    const s = r.reduce((a, b) => a + b.a, 0);
    if (rect.w >= rect.h) {
      const rw = s / rect.h;
      let yy = rect.y;
      r.forEach(d => { const hh = d.a / rw;
        out.push({ ...d, x: rect.x, y: yy, w: rw, h: hh }); yy += hh; });
      rect = { x: rect.x + rw, y: rect.y, w: rect.w - rw, h: rect.h };
    } else {
      const rh = s / rect.w;
      let xx = rect.x;
      r.forEach(d => { const ww = d.a / rh;
        out.push({ ...d, x: xx, y: rect.y, w: ww, h: rh }); xx += ww; });
      rect = { x: rect.x, y: rect.y + rh, w: rect.w, h: rect.h - rh };
    }
  };

  for (const d of queue) {
    if (!row.length) { row.push(d); continue; }
    const len = Math.min(rect.w, rect.h);
    if (worst(row, len) >= worst(row.concat(d), len)) row.push(d);
    else { flush(row); row = [d]; }
  }
  if (row.length) flush(row);
  return out;
}

const TOP_SECTORS = 12;

const SectorTreemap = ({ rows, dark, untypedColor, onOpenMapping }) => {
  const T = useChartTheme();
  const wrap = useRef(null);
  const [w, setW] = useState(760);
  const [hi, setHi] = useState(null);
  useEffect(() => {
    if (!wrap.current || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(es => setW(Math.max(320, es[0].contentRect.width)));
    ro.observe(wrap.current); return () => ro.disconnect();
  }, []);

  const shaped = useMemo(() => {
    const priced = rows.filter(r => num(r.volume) > 0);
    // Named and counted below the chart. A sector whose deals were all
    // undisclosed has a real presence in the market and zero area here.
    const unpriced = rows.filter(r => num(r.volume) <= 0);

    const unmapped = priced.filter(r => r.unmapped);
    const named = priced.filter(r => !r.unmapped)
      .sort((a, b) => num(b.volume) - num(a.volume));
    const keep = named.slice(0, TOP_SECTORS);
    const rest = named.slice(TOP_SECTORS);

    const tiles = keep.concat(unmapped);
    if (rest.length) {
      // Summed, not averaged: "other" is one aggregate sector and its bank
      // share is its own volumes divided, exactly like every other tile.
      const sum = k => rest.reduce((s, r) => s + num(r[k]), 0);
      tiles.push({ rollup: `Other (${rest.length} sector${rest.length === 1 ? "" : "s"})`,
                   other: true, members: rest.map(r => r.rollup),
                   deals: sum("deals"), volume: sum("volume"),
                   banks: sum("banks"), nonbanks: sum("nonbanks"), untyped: sum("untyped"),
                   bankVolume: sum("bankVolume"), nonbankVolume: sum("nonbankVolume"),
                   untypedVolume: sum("untypedVolume") });
    }
    return { tiles: tiles.sort((a, b) => num(b.volume) - num(a.volume)), unpriced };
  }, [rows]);

  const laid = useMemo(() => squarify(
    shaped.tiles.map((t, i) => ({ i, v: num(t.volume) })), 0, 0, w, TREE_H),
    [shaped, w]);

  if (!rows.length) return <Empty />;
  const grand = shaped.tiles.reduce((s, t) => s + num(t.volume), 0);
  const bankShare = t => (num(t.volume) ? num(t.bankVolume) / num(t.volume) : null);

  return (
    <div ref={wrap} style={{ position: "relative" }}>
      <svg width="100%" height={TREE_H} viewBox={`0 0 ${w} ${TREE_H}`} role="img"
           aria-label="Sector volume by area, bank share by colour"
           onMouseLeave={() => setHi(null)}>
        <defs>
          <pattern id="ic-hatch-tree" width="7" height="7" patternTransform="rotate(45)"
                   patternUnits="userSpaceOnUse">
            <rect width="7" height="7" fill={untypedColor} opacity="0.28" />
            <line x1="0" y1="0" x2="0" y2="7" stroke={untypedColor} strokeWidth="3" />
          </pattern>
        </defs>
        {laid.map(box => {
          const t = shaped.tiles[box.i];
          const share = bankShare(t);
          const step = shareStep(share == null ? 0.5 : share, dark);
          // The unmapped tile is not a sector, it is a job. Texture rather than
          // a step on the scale, because colouring it by bank share would put a
          // data-quality bucket on the same footing as Healthcare.
          const fill = t.unmapped ? "url(#ic-hatch-tree)" : step.fill;
          const ink = t.unmapped ? T.ink : step.ink;
          // A 2px surface gap between fills, so a boundary is a line rather
          // than a colour change — the one place adjacent tiles are ambiguous.
          const bx = box.x + 1, by = box.y + 1;
          const bw = Math.max(0, box.w - 2), bh = Math.max(0, box.h - 2);
          const roomy = bw >= 74 && bh >= 30;
          const veryRoomy = bw >= 96 && bh >= 52;
          return (
            <g key={t.rollup} onMouseEnter={() => setHi(box.i)}
               onClick={() => { if (t.unmapped && onOpenMapping) onOpenMapping(); }}
               style={{ cursor: t.unmapped && onOpenMapping ? "pointer" : "default" }}>
              <rect x={bx} y={by} width={bw} height={bh} fill={fill} rx="3"
                    stroke={hi === box.i ? T.ink : "transparent"} strokeWidth="1.5" />
              {roomy ? (
                <text x={bx + 8} y={by + 18} fontSize="12" fontWeight="600" fill={ink}>
                  {t.rollup.length > Math.floor(bw / 7)
                    ? t.rollup.slice(0, Math.max(3, Math.floor(bw / 7) - 1)) + "…"
                    : t.rollup}
                </text>
              ) : null}
              {veryRoomy ? (
                <>
                  <text x={bx + 8} y={by + 35} fontSize="12" fill={ink} opacity="0.9">
                    {money(t.volume)} · {pct(num(t.volume) / grand)}
                  </text>
                  {t.unmapped ? (
                    <text x={bx + 8} y={by + 51} fontSize="11" fill={ink} opacity="0.75">
                      needs mapping →
                    </text>
                  ) : share == null ? null : (
                    <text x={bx + 8} y={by + 51} fontSize="11" fill={ink} opacity="0.75">
                      {pct(share)} bank
                    </text>
                  )}
                </>
              ) : null}
            </g>
          );
        })}
      </svg>

      {hi !== null && shaped.tiles[hi] ? (() => {
        const t = shaped.tiles[hi];
        const box = laid.find(b => b.i === hi) || { x: 0, w: 0 };
        const s = bankShare(t);
        return (
          <Tooltip T={T} x={((box.x + box.w / 2) / w) * 100}>
            <strong>{t.rollup}</strong>
            <div>{money(t.volume)} · {pct(num(t.volume) / grand)} of volume</div>
            <div>{t.deals} closing{t.deals === 1 ? "" : "s"}</div>
            {s == null ? null : <div>{pct(s)} bank / {pct(1 - s)} non-bank by volume</div>}
            {t.untyped ? <div style={{ color: T.muted }}>
              {t.untyped} with the lender not typed</div> : null}
            {t.other ? <div style={{ color: T.muted, maxWidth: 260 }}>
              {t.members.slice(0, 6).join(", ")}
              {t.members.length > 6 ? `, +${t.members.length - 6} more` : ""}</div> : null}
            {t.unmapped ? <div style={{ color: T.muted }}>
              Click to map these industries</div> : null}
          </Tooltip>
        );
      })() : null}

      <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center",
                    marginTop: 10, fontSize: 12, color: T.muted }}>
        <span style={{ fontWeight: 600 }}>Bank share of volume</span>
        {SHARE_SCALE[dark ? "dark" : "light"].map(s => (
          <span key={s.label} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <span style={{ width: 14, height: 12, borderRadius: 2, background: s.fill,
                           border: `1px solid ${T.gridFirm}` }} />
            {s.label}
          </span>
        ))}
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 14, height: 12, borderRadius: 2,
                         border: `1px solid ${T.gridFirm}`,
                         background: `repeating-linear-gradient(45deg, ${untypedColor}55 0 3px, transparent 3px 6px)` }} />
          Not yet mapped
        </span>
      </div>

      {shaped.unpriced.length ? (
        <Note T={T}>
          {shaped.unpriced.length} sector(s) have closings but no disclosed volume, so
          they have no area on this chart and are not shown:{" "}
          {shaped.unpriced.map(r => `${r.rollup} (${r.deals})`).join(", ")}.
          They are in the table below.
        </Note>
      ) : null}
    </div>
  );
};

// Magnitude against identity, with long text labels — a table with the bar
// drawn inside it, rather than a bar chart with a legend nobody can match up.
// SERVER-SORTED. This is a TOP 25 — the sort chooses which lenders appear, so
// it has to happen where the other 1,200 are.
const LenderTable = ({ rows, color, sorter, onSort }) => {
  if (!rows.length) return <Empty />;
  // The bar is scaled to the largest DEAL COUNT on screen whatever the sort,
  // so it always means the same thing. Rescaling it to whichever column is
  // sorted would make the same lender's bar change length for no reason.
  const max = Math.max(...rows.map(r => r.deals), 1);
  const th = (id, label, opts) => (
    <SortTh id={id} sorter={sorter} onSort={onSort}
            align={opts && opts.align} numeric={opts && opts.numeric}>{label}</SortTh>
  );
  return (
    <table style={tableStyle}>
      <thead><tr>
        {th("lender", "Lender")}
        {th("type", "Type")}
        {th("deals", "Closings", { align: "right", numeric: true })}
        {th("volume", "Volume", { align: "right", numeric: true })}
        {th("avgDeal", "Average", { align: "right", numeric: true })}
        {th("lastSeen", "Last seen", { numeric: true })}
      </tr></thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.lender}>
            <Td>
              <div style={{ position: "relative", paddingRight: 8 }}>
                <div style={{ position: "absolute", left: 0, top: 2, bottom: 2,
                              width: `${(r.deals / max) * 100}%`, background: color,
                              opacity: 0.14, borderRadius: 3 }} />
                <span style={{ position: "relative" }}>{r.lender}</span>
                {r.competitor === "Yes" ? (
                  <span style={badge}>competitor</span>
                ) : null}
              </div>
            </Td>
            <Td>{r.type || <span style={{ color: INK_MUTED }}>—</span>}</Td>
            <Td align="right">{r.deals}</Td>
            <Td align="right">{money(r.volume)}</Td>
            <Td align="right">{r.avgDeal == null ? "—" : money(r.avgDeal)}</Td>
            <Td>{r.lastSeen || "—"}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// CLIENT-SORTED. byRollup is the complete set — no TOP, no paging — so the
// browser holds every sector and sorting is a comparison, not a request.
//
// The bar stays because a sector list is a magnitude comparison first and a
// lookup second; it is scaled to the largest DEAL COUNT regardless of the sort,
// so its length always means the same thing.
const SectorTable = ({ rows, color }) => {
  const sorter = useSorter("deals", "desc");
  if (!rows.length) return <Empty />;
  const max = Math.max(...rows.map(r => r.deals), 1);
  const get = (r, k) => (k === "rollup" ? r.rollup : k === "volume" ? r.volume
                       : k === "avgDeal" ? (r.deals ? r.volume / r.deals : null) : r.deals);
  const sorted = sortBy(rows, sorter.key, sorter.dir, get);
  return (
    <table style={tableStyle}>
      <thead><tr>
        <SortTh id="rollup" sorter={sorter}>Sector</SortTh>
        <SortTh id="deals" sorter={sorter} align="right" numeric>Closings</SortTh>
        <SortTh id="volume" sorter={sorter} align="right" numeric>Volume</SortTh>
        <SortTh id="avgDeal" sorter={sorter} align="right" numeric>Average</SortTh>
        <Th />
      </tr></thead>
      <tbody>
        {sorted.map(r => (
          <tr key={r.rollup}>
            <Td>{r.rollup}</Td>
            <Td align="right">{r.deals}</Td>
            <Td align="right">{money(r.volume)}</Td>
            <Td align="right">{r.deals ? money(r.volume / r.deals) : "—"}</Td>
            <Td>
              <div style={{ background: SURFACE_MUTED, borderRadius: 4, height: 10,
                            minWidth: 90 }}>
                <div style={{ width: `${(r.deals / max) * 100}%`, height: 10,
                              background: color, borderRadius: 4 }} />
              </div>
            </Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// "DISCLOSED", NOT "PRICED". The column is COUNT(AMOUNT) — how many of that
// period's closings published a dollar figure at all. ABF Journal prints a lot
// of "undisclosed", so it is the difference between "volume fell" and "fewer
// deals said what they were worth", which is the first thing to check when a
// bar drops. "Priced" read as something about the coupon.
const QuarterTable = ({ rows, changeLabel }) => (
  <table style={tableStyle}>
    <thead><tr>
      <Th>Period</Th><Th align="right">Closings</Th>
      <Th align="right">Disclosed</Th>
      <Th align="right">Volume</Th>
      <Th align="right">{changeLabel || "Change"}</Th>
      <Th align="right">Average</Th>
      <Th align="right">Bank</Th><Th align="right">Non bank</Th>
      <Th align="right">Bank share</Th>
    </tr></thead>
    <tbody>
      {rows.map(r => (r.missing ? (
        <tr key={r.period}>
          <Td>{periodLabel(r.period)}</Td>
          <Td align="right" colSpan={8}>
            <span style={{ color: INK_MUTED }}>no closings on file</span>
          </Td>
        </tr>
      ) : (
        <tr key={r.period}>
          <Td>{periodLabel(r.period)}</Td>
          <Td align="right">{r.deals}</Td>
          <Td align="right">{r.priced}</Td>
          <Td align="right">{moneyFull(r.volume)}</Td>
          <Td align="right">{r.change == null ? "—" : signedPct(r.change)}</Td>
          <Td align="right">{r.priced ? moneyFull(r.volume / r.priced) : "—"}</Td>
          <Td align="right">{moneyFull(r.bankVolume)}</Td>
          <Td align="right">{moneyFull(r.nonbankVolume)}</Td>
          <Td align="right">
            {num(r.volume) ? pct(num(r.bankVolume) / num(r.volume)) : "—"}
          </Td>
        </tr>
      )))}
    </tbody>
  </table>
);

// SERVER-SORTED. This grid is one page of a set that runs to thousands, so a
// sort has to reach past the page — sorting 100 rows in the browser would rank
// them within the page and call it the answer.
const DealTable = ({ rows, sorter, onSort, empty, onEdit }) => {
  if (!rows.length) return <Empty>{empty}</Empty>;
  const th = (id, label, opts) => (
    <SortTh id={id} sorter={sorter} onSort={onSort}
            align={opts && opts.align} numeric={opts && opts.numeric}>{label}</SortTh>
  );
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={tableStyle}>
        <thead><tr>
          {th("period", "Period", { numeric: true })}
          {th("date", "Date", { numeric: true })}
          {th("borrower", "Borrower")}
          {th("industry", "Industry")}
          {th("lender", "Lender")}
          {th("type", "Type")}
          {th("amount", "Amount", { align: "right", numeric: true })}
          <Th>Structure</Th><Th /><Th /></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <Td>{periodLabel(r.period) || "—"}</Td>
              <Td>{r.date || <span style={{ color: INK_MUTED }}>—</span>}</Td>
              <Td>{r.borrower}</Td>
              <Td>{r.rollup || r.industry || "—"}</Td>
              <Td>{r.lender}{r.competitor === "Yes" ? <span style={badge}>competitor</span> : null}</Td>
              <Td>{r.type || "—"}</Td>
              <Td align="right">{r.amount == null
                ? <span style={{ color: INK_MUTED }}>undisclosed</span>
                : moneyFull(r.amount)}</Td>
              <Td>{r.structure || "—"}</Td>
              <Td>{r.url ? <a href={r.url} target="_blank" rel="noopener noreferrer"
                              style={{ color: B.cyan, fontSize: 12 }}>article</a> : null}</Td>
              <Td>{onEdit ? (
                <button type="button" onClick={() => onEdit(r)}
                        style={{ border: "none", background: "transparent", cursor: "pointer",
                                 color: B.cyan, fontSize: 12, padding: 0 }}>edit</button>
              ) : null}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ═══ loading a deal chart ═════════════════════════════════════════════════
//
// Split in two so it can sit in two places: a COMPACT CONTROL in the corner of
// the filter card, and the FLOW it opens underneath. Both work on the same
// piece of page state, so there is one upload in progress at a time and no way
// for the corner to disagree with the panel.
//
// Three steps, and the middle one is the point: NOTHING IS WRITTEN UNTIL A
// PREVIEW HAS BEEN LOOKED AT. The preview returns a key derived from the file,
// the chosen tabs and the amount units; commit sends it back and the server
// refuses if any of them changed. So the numbers that get written are the ones
// that were on screen.

// Shared by both halves, so the request shape is defined once.
const uploadCall = (api, s, body) => api.post("uploadIndustryClosings",
  { filedata: s.filedata, bareM: s.bareM || "million", ...body });

const readSheets = (api, s, set) => {
  set({ busy: true, err: "" });
  uploadCall(api, s, { action: "sheets" })
    .then(d => set({ busy: false, sheets: d.sheets, preview: null,
      // Pre-select every importable tab and adopt each one's detected units.
      // The detection is still not APPLIED without this being visible: the
      // units sit in a dropdown per tab, showing what will be used.
      picked: d.sheets.filter(x => x.importable).map(x => x.name),
      units: Object.fromEntries(d.sheets.filter(x => x.importable)
        .map(x => [x.name, x.detectedUnits || "dollars"])) }))
    .catch(e => set({ busy: false, err: e.message }));
};

// ── the corner control ─────────────────────────────────────────────────────
//
// A button, not a bare <input type="file">. The native control renders as a
// grey "Choose File · No file chosen" block that is a different size in every
// browser and looks like scaffolding next to the filter selects — fine at the
// bottom of a page, wrong in the corner of a card. The input is still there,
// hidden, doing the actual work.
//
// Reading the tabs happens on selection rather than on a second click: there is
// nothing to decide between the two, and the sheet list is what the reader
// actually asked for when they picked the file.
const UploadControl = ({ api, state, setState }) => {
  const s = state || {};
  const set = patch => setState({ ...s, ...patch });
  const input = useRef(null);

  const readFile = file => {
    const fr = new FileReader();
    fr.onload = () => {
      const next = {
        filedata: String(fr.result).replace(/^data:.*;base64,/, ""),
        filename: file.name, sheets: null, preview: null, err: "",
        picked: [], units: {}, committed: false,
      };
      setState(next);
      readSheets(api, next, patch => setState({ ...next, ...patch }));
    };
    fr.readAsDataURL(file);
  };

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      {s.filename ? (
        <span style={{ fontSize: 12, color: INK_MUTED, maxWidth: 220,
                       overflow: "hidden", textOverflow: "ellipsis",
                       whiteSpace: "nowrap" }} title={s.filename}>
          {s.filename}
        </span>
      ) : null}
      {s.busy ? <Spinner /> : null}
      <input ref={input} type="file" accept=".xlsx" style={{ display: "none" }}
             onChange={e => {
               const file = e.target.files && e.target.files[0];
               // Cleared so choosing the SAME file again still fires a change
               // event — otherwise a re-pick after a failed load does nothing
               // and looks like the button is broken.
               e.target.value = "";
               if (file) readFile(file);
             }} />
      <Btn variant="primary" onClick={() => input.current && input.current.click()} disabled={s.busy}>
        {s.filename ? "Load another" : "Load Data"}
      </Btn>
      {s.filedata ? (
        <Btn variant="default" onClick={() => setState(null)} title="Close">Close</Btn>
      ) : null}
    </div>
  );
};

// ── the flow it opens ──────────────────────────────────────────────────────
const UploadFlow = ({ api, state, setState, onCommitted }) => {
  const s = state || {};
  const set = patch => setState({ ...s, ...patch });

  const doPreview = () => {
    set({ busy: true, err: "" });
    uploadCall(api, s, { action: "preview", sheets: s.picked, units: s.units })
      .then(d => set({ busy: false, preview: d }))
      .catch(e => set({ busy: false, err: e.message }));
  };

  const doCommit = () => {
    set({ busy: true, err: "" });
    uploadCall(api, s, { action: "commit", sheets: s.picked, units: s.units,
                         confirm: true, previewKey: s.preview.previewKey })
      .then(d => {
        // Recorded FIRST and unconditionally. Whatever happens next, the rows
        // are in — and the screen must say so.
        set({ busy: false, preview: d, committed: true, err: "", refreshErr: "" });
        // THE WRITE AND THE REFRESH ARE TWO DIFFERENT FACTS. onCommitted()
        // reloads the charts, and it used to be called inside this .then with
        // nothing between them — so an error thrown while REFRESHING landed in
        // the page's error banner and read as "the write failed" when the rows
        // were already committed. That is the worst thing to be wrong about:
        // it invites a retry of an import that already happened.
        try {
          const r = onCommitted();
          if (r && typeof r.catch === "function") {
            r.catch(e => setState(cur => ({ ...(cur || {}),
              refreshErr: e.message || String(e) })));
          }
        } catch (e) {
          setState(cur => ({ ...(cur || {}), refreshErr: e.message || String(e) }));
        }
      })
      .catch(e => set({ busy: false, err: e.message || String(e) }));
  };

  const p = s.preview;
  return (
    <div>
      <div style={{ fontSize: 12, color: INK_MUTED, marginBottom: 8 }}>
        Nothing is written until you have seen a preview.
      </div>
      {s.err ? <ErrorMsg msg={s.err} /> : null}

      {s.sheets ? (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr>
              <Th /><Th>Tab</Th><Th align="right">Rows</Th><Th>Quarter</Th>
              <Th>Amounts are in</Th><Th /></tr>
            </thead>
            <tbody>
              {s.sheets.map(sh => (
                <tr key={sh.name}>
                  <Td>
                    <input type="checkbox" disabled={!sh.importable}
                           checked={(s.picked || []).includes(sh.name)}
                           onChange={e => set({ picked: e.target.checked
                             ? [...(s.picked || []), sh.name]
                             : (s.picked || []).filter(n => n !== sh.name) })} />
                  </Td>
                  <Td>{sh.name}</Td>
                  <Td align="right">{sh.importable ? sh.rows : "—"}</Td>
                  <Td>
                    {sh.period || "—"}
                    {sh.periodFromTabName && sh.period && sh.periodFromTabName !== sh.period
                      ? <span style={{ ...badge, background: "transparent",
                                       border: `1px solid ${HAIRLINE_FIRM}` }}>
                          tab says {sh.periodFromTabName}</span>
                      : null}
                  </Td>
                  <Td>
                    {sh.importable ? (
                      <select value={(s.units || {})[sh.name] || "dollars"}
                              onChange={e => set({ units: { ...(s.units || {}),
                                                            [sh.name]: e.target.value } })}
                              style={inputStyle}>
                        <option value="dollars">full dollars</option>
                        <option value="thousands">thousands</option>
                        <option value="millions">millions</option>
                      </select>
                    ) : "—"}
                    {sh.detectedUnits && sh.detectedUnits !== ((s.units || {})[sh.name])
                      ? <span style={{ color: INK_MUTED, fontSize: 11, marginLeft: 6 }}>
                          looks like {sh.detectedUnits}</span>
                      : null}
                  </Td>
                  <Td>
                    {(sh.notices || []).map((n, i) => (
                      <div key={i} style={{ color: INK_MUTED, fontSize: 11 }}>{n}</div>
                    ))}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 10 }}>
            <Btn variant="primary" onClick={doPreview} disabled={s.busy || !(s.picked || []).length}>
              Preview {(s.picked || []).length} tab{(s.picked || []).length === 1 ? "" : "s"}
            </Btn>
          </div>
        </div>
      ) : null}

      {p ? (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <Stat label="New" value={p.rows.new} />
            <Stat label="Already stored" value={p.rows.duplicate} />
            <Stat label="Repeated in file" value={p.rows.repeatedInFile} />
            <Stat label="Amended figure" value={p.rows.amended} />
          </div>
          <Note>{p.enrichment.summary}</Note>
          {p.enrichment.unknownLenders.length ? (
            <Note>
              New lenders, left blank for you to type: {" "}
              {p.enrichment.unknownLenders.slice(0, 12).map(u => u.lender).join(", ")}
              {p.enrichment.unknownLenders.length > 12
                ? ` and ${p.enrichment.unknownLenders.length - 12} more` : ""}.
              {" "}Once you set one on a row, the next upload finds it.
            </Note>
          ) : null}
          {(p.sheets || []).filter(x => x.ok && (x.notices || []).length).map(x => (
            <Note key={x.sheet}><strong>{x.sheet}</strong> — {x.notices.join(" ")}</Note>
          ))}
          {p.committed ? (
            <>
              <Note><strong>Written.</strong> {p.inserted} row(s) added.
                {" "}Do not upload this file again — a second pass would find them
                already stored, but there is no need to check.</Note>
              {/* A failed refresh is not a failed write, and must never be
                  mistaken for one. */}
              {s.refreshErr ? (
                <Note>
                  <strong>The rows were saved.</strong> Reloading the figures on this
                  page failed, so the charts above are stale — reload the page to see
                  them. Nothing needs re-importing. ({s.refreshErr})
                </Note>
              ) : null}
            </>
          ) : (
            <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
              <Btn variant="primary" onClick={doCommit} disabled={s.busy || !p.rows.new}>
                Write {p.rows.new} row{p.rows.new === 1 ? "" : "s"}
              </Btn>
              {s.busy ? <Spinner /> : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

// ═══ small pieces ═════════════════════════════════════════════════════════

const axisTicks = (max, n) => {
  const step = niceStep(max / n);
  const out = [];
  for (let v = 0; v <= max * 1.0001; v += step) out.push(v);
  return out;
};
const niceStep = raw => {
  const mag = Math.pow(10, Math.floor(Math.log10(raw || 1)));
  const norm = (raw || 1) / mag;
  return (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10) * mag;
};

const Tooltip = ({ x, children, T }) => (
  <div style={{ position: "absolute", top: 4, left: `${Math.min(78, Math.max(2, x))}%`,
                background: T ? T.tipBg : SURFACE,
                border: `1px solid ${T ? T.tipBorder : HAIRLINE_FIRM}`, borderRadius: 6,
                padding: "6px 9px", fontSize: 12, color: T ? T.ink : INK_PRIMARY, pointerEvents: "none",
                boxShadow: T ? T.tipShadow : "0 2px 8px rgba(0,0,0,0.10)", whiteSpace: "nowrap" }}>
    {children}
  </div>
);

const Legend = ({ items, T }) => (
  <div style={{ display: "flex", gap: 14, marginTop: 8, flexWrap: "wrap" }}>
    {items.map(i => (
      <span key={i.label} style={{ display: "inline-flex", alignItems: "center", gap: 6,
                                   fontSize: 12, color: T ? T.muted : INK_MUTED }}>
        {/* The hatched swatch is drawn on a tinted ground, not by fading the
            colour: a 45%-opacity square reads as "almost white" at 12px and the
            key for the missing-data segment becomes invisible. */}
        <span style={{ width: 12, height: 12, borderRadius: 3,
                       backgroundColor: i.hatch ? `${i.color}33` : i.color,
                       backgroundImage: i.hatch
                         ? `repeating-linear-gradient(45deg, ${i.color} 0 2.5px, transparent 2.5px 6px)`
                         : "none" }} />
        {i.label}
      </span>
    ))}
  </div>
);

// T (a chart theme) only on chart cards; tables and panels stay light.
const ChartHead = ({ title, sub, right, T }) => (
  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                gap: 12, marginBottom: 6 }}>
    <div>
      <div style={{ fontWeight: 600, color: T ? T.ink : INK_PRIMARY }}>{title}</div>
      {sub ? <div style={{ fontSize: 12, color: T ? T.muted : INK_MUTED }}>{sub}</div> : null}
    </div>
    {right}
  </div>
);

const Stat = ({ label, value, note }) => (
  <Card style={{ padding: 12 }}>
    <div style={{ fontSize: 12, color: INK_MUTED }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 600, color: INK_PRIMARY }}>{value}</div>
    {note ? <div style={{ fontSize: 11, color: INK_MUTED }}>{note}</div> : null}
  </Card>
);

const Note = ({ children, T }) => (
  <div style={{ marginTop: 10, padding: "8px 10px", background: T ? T.panel : SURFACE_MUTED,
                border: `1px solid ${T ? T.grid : HAIRLINE}`, borderRadius: 6,
                fontSize: 12, color: T ? T.ink : INK_PRIMARY }}>{children}</div>
);

const Empty = ({ children }) => (
  <div style={{ padding: 24, textAlign: "center", color: INK_MUTED, fontSize: 13 }}>
    {children || "Nothing matches these filters."}
  </div>
);

// Month, quarter or year, as buttons rather than a dropdown: three options,
// all worth seeing without opening anything. The toggle also decides what the
// change line means — MoM, QoQ or YoY — so it is the whole of the difference
// between the three PowerBI pages this replaces.
const GrainToggle = ({ value, setValue }) => (
  <span style={{ display: "inline-flex", border: `1px solid ${HAIRLINE_FIRM}`,
                 borderRadius: 6, overflow: "hidden" }}>
    {[["month", "Month"], ["quarter", "Quarter"], ["year", "Year"]].map(([k, label]) => (
      <button key={k} type="button" onClick={() => setValue(k)}
              aria-pressed={value === k}
              style={{ border: "none", cursor: "pointer", padding: "5px 11px", fontSize: 13,
                       fontWeight: value === k ? 600 : 400,
                       background: value === k ? B.cyan : "transparent",
                       color: value === k ? "#fff" : INK_MUTED }}>
        {label}
      </button>
    ))}
  </span>
);

const Tabs = ({ value, setValue, tabs }) => (
  <div style={{ display: "flex", gap: 4, marginTop: 14, borderBottom: `1px solid ${HAIRLINE}` }}>
    {tabs.map(([k, label]) => (
      <button key={k} onClick={() => setValue(k)}
              style={{ border: "none", background: "transparent", cursor: "pointer",
                       padding: "8px 12px", fontSize: 13,
                       color: value === k ? INK_PRIMARY : INK_MUTED,
                       fontWeight: value === k ? 600 : 400,
                       borderBottom: `2px solid ${value === k ? B.cyan : "transparent"}` }}>
        {label}
      </button>
    ))}
  </div>
);

// MATCHES THE HOUSE FILTER STYLE. shared/ui's FilterSelect renders its label
// inline, uppercase, bold and letter-spaced — that is what COMPANY, STATUS and
// FACILITY look like on the other analytics pages. This page could not use
// FilterSelect itself (its filters are multi-selects and a range, which that
// component does not do) but there is no reason for it to look different, and
// a stacked lowercase label was the whole of why it did.
//
// This row once read slate while every other filter row read black, and the
// cause was not a different colour — it was the same token spelled two ways.
// FilterSelect wrote a bare var(--color-text-secondary); this wrote INK_MUTED,
// which is that variable WITH a fallback. The variable was defined nowhere, so
// the bare one was invalid at computed-value time and `color`, being inherited,
// took the parent's near-black, while the fallback here rendered as intended.
// index.css now defines the variable and FilterSelect now goes through theme.js,
// so both spellings resolve to the same value and this is INK_MUTED again.
const FILTER_LABEL = {
  fontSize: 11, fontWeight: 700, color: INK_MUTED,
  textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap",
};

const Field = ({ label, children }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
    <span style={FILTER_LABEL}>{label}</span>
    {children}
  </label>
);

// ── DATES ──────────────────────────────────────────────────────────────────
//
// Two native date inputs and a short preset list. The presets are here for the
// same reason the size ones are: the cuts people actually take are a short
// list, and "last twelve months" typed as two ISO dates is four keystrokes of
// arithmetic nobody should do.
//
// EACH BOX BOUNDS THE OTHER. `max` on the start box and `min` on the end box
// mean the picker itself refuses an inverted range, so "1/1/2026 to 1/1/2025"
// — which returns nothing, for a reason the empty page cannot explain — is not
// reachable by accident.
const iso = d => d.toISOString().slice(0, 10);
const DATE_PRESETS = [
  // First, and named the same way the size menu names its default, so getting
  // back to where the page opened is one click rather than a retyped date.
  { label: "Since 1 Jan 2025", of: () => [DEFAULT_DATE_FROM, ""] },
  { label: "Last 12 months", of: () => { const t = new Date();
      const s = new Date(Date.UTC(t.getUTCFullYear() - 1, t.getUTCMonth(), 1));
      return [iso(s), iso(t)]; } },
  { label: "Year to date", of: () => { const t = new Date();
      return [`${t.getUTCFullYear()}-01-01`, iso(t)]; } },
  { label: "Last calendar year", of: () => { const y = new Date().getUTCFullYear() - 1;
      return [`${y}-01-01`, `${y}-12-31`]; } },
  { label: "Last 3 years", of: () => { const t = new Date();
      const s = new Date(Date.UTC(t.getUTCFullYear() - 3, t.getUTCMonth(), 1));
      return [iso(s), iso(t)]; } },
  { label: "Last 5 years", of: () => { const t = new Date();
      const s = new Date(Date.UTC(t.getUTCFullYear() - 5, t.getUTCMonth(), 1));
      return [iso(s), iso(t)]; } },
];

const DateRange = ({ from, to, setFrom, setTo, min, max }) => {
  const [open, setOpen] = useState(false);
  useEscape(open ? () => setOpen(false) : null);
  return (
    <Field label="Dates">
      <div style={{ display: "flex", gap: 4, alignItems: "center", position: "relative" }}>
        <input type="date" value={from} min={min || undefined} max={to || max || undefined}
               onChange={e => setFrom(e.target.value)}
               style={{ ...inputStyle, width: 132 }} aria-label="From date" />
        <span style={{ color: INK_MUTED }}>–</span>
        <input type="date" value={to} min={from || min || undefined} max={max || undefined}
               onChange={e => setTo(e.target.value)}
               style={{ ...inputStyle, width: 132 }} aria-label="To date" />
        <button type="button" onClick={() => setOpen(o => !o)} title="Common date ranges"
                style={{ ...inputStyle, cursor: "pointer", padding: "5px 7px" }}>▾</button>
        {open ? (
          <>
            <div onClick={() => setOpen(false)}
                 style={{ position: "fixed", inset: 0, zIndex: 9 }} />
            <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 10,
                          background: SURFACE, border: `1px solid ${HAIRLINE_FIRM}`,
                          borderRadius: 8, padding: 4, minWidth: 180,
                          boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }}>
              {DATE_PRESETS.map(p => (
                <button key={p.label} type="button"
                        onClick={() => { const [a, b] = p.of(); setFrom(a); setTo(b); setOpen(false); }}
                        style={{ display: "block", width: "100%", textAlign: "left",
                                 border: "none", background: "transparent", cursor: "pointer",
                                 padding: "6px 8px", fontSize: 12, color: INK_PRIMARY }}>
                  {p.label}
                </button>
              ))}
              <button type="button"
                      onClick={() => { setFrom(""); setTo(""); setOpen(false); }}
                      style={{ display: "block", width: "100%", textAlign: "left",
                               border: "none", background: "transparent", cursor: "pointer",
                               padding: "6px 8px", fontSize: 12, color: INK_MUTED,
                               borderTop: `1px solid ${HAIRLINE}` }}>
                All dates on file
              </button>
            </div>
          </>
        ) : null}
      </div>
    </Field>
  );
};

// ── FINANCING SIZE ─────────────────────────────────────────────────────────
//
// Typed in MILLIONS. The column holds dollars, but nobody filtering for deals
// in the twenty-five to fifty range wants to count zeros, and the unit is on
// the label rather than left to be guessed.
//
// Presets beside the boxes because the useful cuts are a short list and most
// of them are one click. "Our range" is first: the size band the lender actually
// lends in is the comparison this page exists to make, and it is the one that
// would otherwise be retyped every visit.
//
// Either bound alone works — a minimum with no maximum is "everything above
// this", which is how a floor is usually meant.
const SIZE_PRESETS = [
  { label: "Our range · $5–50MM", min: DEFAULT_SIZE.min, max: DEFAULT_SIZE.max },
  { label: "Under $10MM",         min: "",    max: "10" },
  { label: "$10–25MM",            min: "10",  max: "25" },
  { label: "$25–50MM",            min: "25",  max: "50" },
  { label: "$50–100MM",           min: "50",  max: "100" },
  { label: "$100MM–$1B",          min: "100", max: "1000" },
  { label: "Over $1B",            min: "1000", max: "" },
];

const SizeRange = ({ min, max, setMin, setMax }) => {
  const [open, setOpen] = useState(false);
  useEscape(open ? () => setOpen(false) : null);
  const active = SIZE_PRESETS.find(p => p.min === min && p.max === max);
  return (
    <Field label="Financing size ($MM)">
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <input value={min} onChange={e => setMin(e.target.value.replace(/[^\d.]/g, ""))}
               placeholder="Min" inputMode="decimal"
               style={{ ...inputStyle, width: 62 }} />
        <span style={{ color: INK_MUTED }}>–</span>
        <input value={max} onChange={e => setMax(e.target.value.replace(/[^\d.]/g, ""))}
               placeholder="Max" inputMode="decimal"
               style={{ ...inputStyle, width: 62 }} />
        <div style={{ position: "relative" }}>
          <button type="button" onClick={() => setOpen(o => !o)}
                  title={active ? active.label : "Common size ranges"}
                  style={{ ...inputStyle, cursor: "pointer", padding: "5px 7px" }}>▾</button>
          {open ? (
            <div style={{ position: "absolute", zIndex: 20, top: "100%", right: 0, marginTop: 4,
                          background: SURFACE, border: `1px solid ${HAIRLINE_FIRM}`,
                          borderRadius: 6, padding: 6, minWidth: 180,
                          boxShadow: "0 4px 14px rgba(0,0,0,0.12)" }}>
              {SIZE_PRESETS.map(p => (
                <button key={p.label} type="button"
                        onClick={() => { setMin(p.min); setMax(p.max); setOpen(false); }}
                        style={{ display: "block", width: "100%", textAlign: "left",
                                 border: "none", background: "transparent", cursor: "pointer",
                                 padding: "4px 6px", fontSize: 13, borderRadius: 4,
                                 color: active === p ? B.cyan : INK_PRIMARY,
                                 fontWeight: active === p ? 600 : 400 }}>
                  {p.label}
                </button>
              ))}
              <button type="button"
                      onClick={() => { setMin(""); setMax(""); setOpen(false); }}
                      style={{ display: "block", width: "100%", textAlign: "left",
                               border: "none", background: "transparent", cursor: "pointer",
                               padding: "4px 6px", fontSize: 12, color: INK_MUTED,
                               borderTop: `1px solid ${HAIRLINE}`, marginTop: 4 }}>
                Any size
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </Field>
  );
};

// Nothing selected means EVERYTHING, which is what an unset filter means to a
// reader. The alternative — an empty selection meaning an empty result — is a
// page that starts blank and has to be filled in before it says anything.
const Multi = ({ label, value, setValue, options, render, allLabel }) => {
  const [open, setOpen] = useState(false);
  useEscape(open ? () => setOpen(false) : null);
  const show = render || (v => v);
  return (
    <Field label={label}>
      <div style={{ position: "relative" }}>
        <button type="button" onClick={() => setOpen(o => !o)} style={{ ...inputStyle,
                textAlign: "left", cursor: "pointer", minWidth: 130 }}>
          {value.length === 0 ? (allLabel || "All") : value.length === 1 ? show(value[0])
            : `${value.length} selected`}
        </button>
        {open ? (
          <div style={{ position: "absolute", zIndex: 20, top: "100%", left: 0, marginTop: 4,
                        background: SURFACE, border: `1px solid ${HAIRLINE_FIRM}`,
                        borderRadius: 6, padding: 8, maxHeight: 260, overflowY: "auto",
                        minWidth: 200, boxShadow: "0 4px 14px rgba(0,0,0,0.12)" }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              <button type="button" style={linkBtn}
                      onClick={() => setValue([])}>{allLabel || "All"}</button>
              <button type="button" style={linkBtn} onClick={() => setOpen(false)}>Done</button>
            </div>
            {options.map(o => (
              <label key={o} style={{ display: "flex", gap: 6, alignItems: "center",
                                      fontSize: 13, padding: "2px 0" }}>
                <input type="checkbox" checked={value.includes(o)}
                       onChange={e => setValue(e.target.checked
                         ? [...value, o] : value.filter(v => v !== o))} />
                {show(o)}
              </label>
            ))}
          </div>
        ) : null}
      </div>
    </Field>
  );
};

const inputStyle = { padding: "5px 8px", fontSize: 13, borderRadius: 6,
                     border: `1px solid ${HAIRLINE_FIRM}`, background: SURFACE,
                     color: INK_PRIMARY };
const linkBtn = { border: "none", background: "transparent", color: B.cyan,
                  cursor: "pointer", fontSize: 12, padding: 0 };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 6 };
const badge = { marginLeft: 6, fontSize: 10, padding: "1px 5px", borderRadius: 8,
                background: SURFACE_MUTED, color: INK_MUTED, whiteSpace: "nowrap" };

// ── EDITING ONE CLOSING ────────────────────────────────────────────────────
//
// Administrators only, and the endpoint enforces that independently — this
// form is the convenience, not the control.
//
// THE QUARTER IS NOT ON THIS FORM. closingyear and closingquarter are derived
// from the date, server-side, and there is deliberately no way to set them by
// hand: the rule has to hold in one place or the charts and the data drift
// apart one edit at a time. The period is shown, greyed, so you can see what
// the date you typed produced.
//
// AMOUNT IS IN FULL DOLLARS, and the field says so. The importer understands
// "25MM" because a spreadsheet cell is written by someone else; a box that
// currently reads 25,000,000 is not, and "25MM" typed into it is far more
// likely a slip than an intention. The server refuses it rather than guessing.
const EDIT_FIELDS = [
  ["DATE", "Closing date", "date"],
  ["BORROWER", "Borrower"],
  ["INDUSTRY", "Industry (as published)"],
  ["IndustryRollup", "Industry rollup", "rollup"],
  ["LENDER", "Lender (as published)"],
  ["LENDER TRIM", "Lender, trimmed"],
  ["TYPE", "Lender type", "type"],
  ["COMPETITOR", "Competitor", "competitor"],
  ["ROLE", "Role"],
  ["AMOUNT", "Amount ($, full dollars)"],
  ["STRUCTURE", "Structure", "long"],
  ["NOTES", "Notes", "long"],
  ["URL", "Article URL"],
  ["Category", "Category"],
  ["Sponsor", "Sponsor"],
  ["Advisor", "Advisor"],
];

// The shared Field renders its label as a SIBLING of the control, with no
// htmlFor — so clicking the label does not focus the field and a screen reader
// does not connect the two. Wrapping the control inside the label fixes both
// without touching the shared component, which the whole app uses.
const EditField = ({ label, children, hint }) => (
  <label style={{ display: "block", marginBottom: 14 }}>
    <span style={{ display: "block", fontSize: 12, fontWeight: 500,
                   color: "#555", marginBottom: 4 }}>{label}</span>
    {children}
    {hint ? <span style={{ display: "block", fontSize: 11, color: INK_MUTED,
                           marginTop: 3 }}>{hint}</span> : null}
  </label>
);

const DealEditor = ({ api, row, vocab, onClose, onSaved }) => {
  const initial = {
    DATE: row.date || "",
    BORROWER: row.borrower || "",
    INDUSTRY: row.industry || "",
    IndustryRollup: row.rollup || "",
    LENDER: row.lenderRaw || row.lender || "",
    "LENDER TRIM": row.lendertrim || "",
    TYPE: row.type || "",
    COMPETITOR: row.competitor || "",
    ROLE: row.role || "",
    AMOUNT: row.amount == null ? "" : String(row.amount),
    STRUCTURE: row.structure || "",
    NOTES: row.notes || "",
    URL: row.url || "",
    Category: row.category || "",
    Sponsor: row.sponsor || "",
    Advisor: row.advisor || "",
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Only what actually changed is sent. A save that rewrote every column would
  // touch fields nobody edited, and the last-writer-wins damage from two people
  // on the same row would be the whole row rather than one field.
  const changed = Object.keys(initial).filter(k => (form[k] || "") !== (initial[k] || ""));

  // Shown, not editable — the consequence of the date above it.
  const derived = (() => {
    const m = String(form.DATE || "").match(/^(\d{4})-(\d{2})-\d{2}$/);
    if (!m) return "no date, so no quarter";
    return `${m[1]}Q${Math.ceil(Number(m[2]) / 3)}`;
  })();

  const save = () => {
    setBusy(true); setErr("");
    const payload = { ClosingID: row.ClosingID };
    changed.forEach(k => { payload[k] = form[k] === "" ? null : form[k]; });
    api.post("saveIndustryClosing", payload)
      .then(d => { setBusy(false); onSaved(d); })
      .catch(e => { setBusy(false); setErr(e.message || String(e)); });
  };

  const opts = (list) => ["", ...(list || [])];

  return (
    <Modal title={`Edit — ${row.borrower || "closing"}`} onClose={onClose} wide>
      {err ? <ErrorMsg msg={err} /> : null}
      <div style={{ display: "grid", gap: 0, gridTemplateColumns: "1fr 1fr", columnGap: 18 }}>
        {EDIT_FIELDS.map(([k, label, kind]) => (
          <EditField key={k} label={label}
                     hint={k === "DATE"
                       ? `Quarter: ${derived} — derived from this date, not editable.`
                       : null}>
            {kind === "type"
              ? <FSelect value={form[k]} onChange={v => set(k, v)} options={opts(vocab.types)} />
              : kind === "competitor"
              ? <FSelect value={form[k]} onChange={v => set(k, v)} options={opts(vocab.competitors)} />
              : kind === "rollup"
              ? (<>
                  {/* A datalist, not a select: the rollup list is open, and a
                      new sector has to be typeable. The list still narrows it
                      so the same sector does not arrive as three spellings. */}
                  <input list="ic-edit-rollups" value={form[k]}
                         onChange={e => set(k, e.target.value)}
                         style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px",
                                  fontSize: 13, borderRadius: 4, border: "1px solid #999" }} />
                  <datalist id="ic-edit-rollups">
                    {(vocab.rollups || []).map(v => <option key={v} value={v} />)}
                  </datalist>
                </>)
              : kind === "long"
              // Structure and Notes routinely run past a line. A single-line
              // input hides the end of what is already stored, which is how an
              // edit silently truncates a field nobody meant to touch.
              ? <textarea value={form[k]} rows={2}
                          onChange={e => set(k, e.target.value)}
                          style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px",
                                   fontSize: 13, borderRadius: 4, border: "1px solid #999",
                                   fontFamily: "inherit", resize: "vertical" }} />
              : <FInput value={form[k]} onChange={v => set(k, v)}
                        type={kind === "date" ? "date" : "text"} />}
          </EditField>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
        <Btn variant="primary" onClick={save} disabled={busy || !changed.length}>
          {changed.length ? `Save ${changed.length} change${changed.length === 1 ? "" : "s"}`
                          : "No changes"}
        </Btn>
        <Btn variant="default" onClick={onClose}>Cancel</Btn>
        {busy ? <Spinner /> : null}
        {changed.length ? (
          <span style={{ fontSize: 11, color: INK_MUTED }}>
            {changed.join(", ")}
          </span>
        ) : null}
      </div>
    </Modal>
  );
};

// ── THE DEAL GRID, WHEREVER IT APPEARS ─────────────────────────────────────
//
// Used twice: under the quarterly charts, narrowed to whichever quarter was
// clicked, and on its own tab across everything. One hook rather than two
// copies, because two copies of paging-plus-sorting-plus-fetching drift, and
// the one that drifts is always the one you are not looking at.
//
// `period` is a string like "2026Q2" or null. It is sent as rowPeriod, which
// the endpoint applies to the rows query ONLY — the charts above keep their
// full range, which is what makes the selection a highlight rather than a
// filter that erases the thing you clicked.
const useDeals = (api, params, period, active) => {
  const sorter = useSorter("period", "desc");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState(0);
  const key = JSON.stringify(params) + "|" + (period || "");

  // Any change of filter, quarter or order returns to the first page: page 3
  // of one list is not a position in another.
  useEffect(() => { setPage(0); }, [key, sorter.key, sorter.dir]);

  const seq = useRef(0);
  useEffect(() => {
    if (!active) return;
    const mine = ++seq.current;
    setBusy(true);
    api.get("getIndustryClosings", {
      ...params, view: "rows", page, pageSize: 100,
      rowSort: sorter.key, rowDir: sorter.dir,
      ...(period ? { rowPeriod: period } : {}),
    })
      .then(d => { if (mine === seq.current) { setRows(d); setBusy(false); } })
      .catch(() => { if (mine === seq.current) setBusy(false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, key, page, sorter.key, sorter.dir, active]);

  // A saved row is patched in place rather than triggering a refetch. The
  // grid keeps its scroll position and its page, and the reader sees the field
  // they just changed change — which a full reload hides behind a spinner.
  // Guarded on the shape of what it is handed. A response that came back
  // without a row — an older API, a proxy that trimmed it — must not take the
  // whole page down with it; the save still happened and the grid just stays
  // one edit behind until the next load.
  const patch = updated => {
    if (!updated || updated.ClosingID == null) return false;
    setRows(cur => (!cur || !Array.isArray(cur.rows) ? cur : {
      ...cur,
      rows: cur.rows.map(r => (r.ClosingID === updated.ClosingID ? { ...r, ...updated } : r)),
    }));
    return true;
  };

  return { rows, busy, page, setPage, sorter, patch };
};

const DealsPanel = ({ deals, title, sub, right, emptyMsg, onEdit }) => {
  const { rows, busy, page, setPage, sorter } = deals;
  const p = rows && rows.paging;
  return (
    <>
      {/* "1–0 of 0" is what the obvious arithmetic produces on an empty
          result, and it reads as a bug. An empty list gets a sentence. */}
      <ChartHead title={title} right={right}
        sub={!p ? (sub || "")
             : !p.total ? "No deals"
             : `${(p.page * p.pageSize + 1).toLocaleString("en-US")}–`
               + `${(p.page * p.pageSize + p.returned).toLocaleString("en-US")}`
               + ` of ${p.total.toLocaleString("en-US")}`} />
      {/* The stale-API check, here too: this grid sorts on the server. */}
      {p && (p.sort !== sorter.key || p.dir !== sorter.dir) ? (
        <Note>
          The API returned these rows in a different order than requested — the
          running <code>getIndustryClosings</code> is probably an older build.
        </Note>
      ) : null}
      {busy && !rows ? <Spinner />
        : <DealTable rows={(rows && rows.rows) || []} sorter={sorter}
                     empty={emptyMsg} onEdit={onEdit} />}
      <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
        <Btn variant="default" disabled={page === 0}
             onClick={() => setPage(x => Math.max(0, x - 1))}>Previous</Btn>
        <Btn variant="default" disabled={!(p && p.more)}
             onClick={() => setPage(x => x + 1)}>Next</Btn>
        {busy ? <Spinner /> : null}
      </div>
    </>
  );
};

// ── SORTING ────────────────────────────────────────────────────────────────
//
// TWO KINDS OF TABLE ON THIS PAGE, and they cannot share a mechanism.
//
//   COMPLETE SETS — the sector mix, and both mapping lists — are entirely in
//   the browser, so sorting them is a comparison and nothing else. Instant, no
//   round trip.
//
//   WINDOWS — the lender league table (a TOP 25) and the deal grid (one page of
//   100) — must sort on the SERVER. Re-ordering a window in JavaScript
//   rearranges the rows you were given rather than choosing which rows to give,
//   so sorting the 25 busiest lenders by volume shows exactly that, not the 25
//   largest by volume. Those are different tables and only one was asked for.
//
// A header click therefore either sorts locally or refetches, and SortTh does
// not need to know which — it reports the click and the caller decides.

// Text ascends on first click, numbers descend: "sort by name" means A first,
// "sort by volume" means the biggest first. Getting this backwards means every
// sort takes two clicks.
const useSorter = (initialKey, initialDir) => {
  const [key, setKey] = useState(initialKey);
  const [dir, setDir] = useState(initialDir || "desc");
  const toggle = (k, numeric) => {
    if (k === key) setDir(d => (d === "asc" ? "desc" : "asc"));
    else { setKey(k); setDir(numeric ? "desc" : "asc"); }
  };
  return { key, dir, toggle };
};

// Nulls sort LAST in both directions. A lender with no type is not "before A"
// and not "after Z" — it is absent, and floating absences to the top of a
// descending sort buries the rows someone actually wanted to see.
const cmp = (a, b, dir) => {
  const nullA = a === null || a === undefined || a === "";
  const nullB = b === null || b === undefined || b === "";
  if (nullA && nullB) return 0;
  if (nullA) return 1;
  if (nullB) return -1;
  let r;
  if (typeof a === "number" && typeof b === "number") r = a - b;
  else r = String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
  return dir === "asc" ? r : -r;
};

const sortBy = (rows, key, dir, get) =>
  [...rows].sort((x, y) => cmp(get(x, key), get(y, key), dir));

const SortTh = ({ id, children, align, numeric, sorter, onSort }) => {
  const active = sorter.key === id;
  return (
    <th aria-sort={active ? (sorter.dir === "asc" ? "ascending" : "descending") : "none"}
        style={{ textAlign: align || "left", padding: 0, fontSize: 11,
                 color: INK_MUTED, fontWeight: 600,
                 borderBottom: `1px solid ${HAIRLINE_FIRM}`, whiteSpace: "nowrap" }}>
      <button type="button"
              onClick={() => { sorter.toggle(id, numeric); if (onSort) onSort(id, numeric); }}
              style={{ border: "none", background: "transparent", cursor: "pointer",
                       font: "inherit", color: active ? INK_PRIMARY : INK_MUTED,
                       padding: "6px 8px", width: "100%",
                       textAlign: align || "left", display: "flex", gap: 4,
                       alignItems: "center",
                       justifyContent: align === "right" ? "flex-end" : "flex-start" }}>
        {children}
        {/* The inactive arrow is present but faint, so the column reads as
            sortable before it is clicked rather than only after. */}
        <span aria-hidden="true" style={{ opacity: active ? 1 : 0.25, fontSize: 9 }}>
          {active ? (sorter.dir === "asc" ? "▲" : "▼") : "▾"}
        </span>
      </button>
    </th>
  );
};

const Th = ({ children, align }) => (
  <th style={{ textAlign: align || "left", padding: "6px 8px", fontSize: 11,
               color: INK_MUTED, fontWeight: 600, borderBottom: `1px solid ${HAIRLINE_FIRM}`,
               whiteSpace: "nowrap" }}>{children}</th>
);
const Td = ({ children, align, colSpan }) => (
  <td colSpan={colSpan}
      style={{ textAlign: align || "left", padding: "6px 8px", color: INK_PRIMARY,
               borderBottom: `1px solid ${HAIRLINE}`,
               fontVariantNumeric: align === "right" ? "tabular-nums" : "normal" }}>{children}</td>
);

export default IndustryClosingsPage;
